import { TEMPLATE_CATEGORY_OPTIONS } from '../TemplateCreation/templateCategory';
import React, { useState, useEffect, Fragment, useMemo, InputHTMLAttributes, forwardRef, useCallback } from 'react';
import { Button, Row, Col, Card, CardBody, Label, FormFeedback, Input, InputGroup, Nav, UncontrolledTooltip, InputGroupText, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import Select, { SingleValue } from 'react-select';
import classnames from "classnames";
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, Download, Edit, Eye, FileText, RotateCcw, Trash2, } from 'react-feather';
import { Controller, set, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { OTP_POPUP_MAX_MS, onOtpPopupClosed, otpProcessingTimeLeft, startOtpProcessing } from './otpProcessing';
import DataTable from 'react-data-table-component';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../Service/crypto';
import 'flatpickr/dist/themes/material_blue.css'; // Import the CSS for the theme you want to use
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../ReaduxToolkit/Hooks';
import ColumnChooser, { ColumnOption, isColVisible } from '../../../Components/ColumnChooser/ColumnChooser';
import { useColumnPreferences } from '../../../hooks/useColumnPreferences';
import ReactPaginate from 'react-paginate';
import { getManualData } from './store';
import websocket_webservice from '../../../Service/websocket_webservice'
import DatePicker from "react-datepicker";
import { FaCalendarAlt } from 'react-icons/fa';
import moment from 'moment-timezone';
import portaldownload from '../../../assets/Avatar/pdf.svg'
import { uniqueUuid } from 'docx';
import regenrate from '../../../assets/Avatar/regenrate.svg'
import api from '../../../api';
import { BotManagerTask, fetchRpaTasks } from '../BotManager/store';

// ── RPA dynamic-header helpers (mirrors Bulkupload.tsx) ─────
interface DynamicInputHeader { key: string; prompt: string; }

const _normalizeBotSteps = (steps: any[] | Record<string, any> | undefined): any[] => {
  if (Array.isArray(steps)) return steps;
  if (steps && typeof steps === 'object') return Object.values(steps);
  return [];
};

const _shouldExcludeField = (key: string, prompt: string, multipleFields?: string[]): boolean => {
  const kl = key.toLowerCase();
  const pl = prompt.toLowerCase();
  const usernameKws = ['username', 'user name', 'user id', 'userid', 'user_id', 'login'];
  const passwordKws = ['password', 'pass word', 'pass', 'pwd', 'passcode'];
  return (
    usernameKws.some((k) => kl.includes(k) || pl.includes(k)) ||
    passwordKws.some((k) => kl.includes(k) || pl.includes(k)) ||
    (multipleFields?.some((f) => f === key) ?? false)
  );
};

const _extractDynamicHeaders = (
  steps: any[] | Record<string, any> | undefined,
  multipleFields?: string[],
): DynamicInputHeader[] => {
  const regex = /\{+([^{}]+?)\}+/g;
  const seen = new Map<string, number>();
  const headers: DynamicInputHeader[] = [];

  // Only placeholders that actually come from the selected template are dynamic
  // fields. Nothing is seeded here — anything the template does not provide is
  // rendered by the default (static) form instead.
  _normalizeBotSteps(steps).forEach((step: any) => {
    const prompt = step?.prompt || step?.description || '';
    if (!prompt) return;
    for (const match of prompt.matchAll(regex)) {
      const baseKey = match[1]?.trim();
      if (!baseKey) continue;
      if (_shouldExcludeField(baseKey, prompt, multipleFields)) continue;
      const count = seen.get(baseKey) || 0;
      seen.set(baseKey, count + 1);
      headers.push({ key: count === 0 ? baseKey : `${baseKey}_${count + 1}`, prompt });
    }
  });
  return headers;
};

// ── Default-field ↔ dynamic-field matching ──────────────────────────────────
// Normalizes a field name/label so "First Name", "first_name" and "FirstName"
// all collapse to the same token.
const _normalizeFieldName = (name: string): string =>
  (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Every default (static) form field mapped to the normalized names a template
// may use for the same piece of data. When a dynamic field matches one of these
// aliases, the default field is hidden so the screen never shows duplicates.
const DEFAULT_FIELD_ALIASES: Record<string, string[]> = {
  firstName: ['firstname', 'fname', 'patientfirstname', 'patientfname'],
  lastName: ['lastname', 'lname', 'patientlastname', 'patientlname'],
  DoB: ['dob', 'dateofbirth', 'birthdate', 'patientdob', 'patientdateofbirth'],
  Memberid: ['memberid', 'memberno', 'membernumber', 'patientmemberid'],
  Provider_or_Company_name: ['providerorcompanyname', 'providername', 'companyname'],
  ProviderNPI: ['providernpi', 'npi', 'groupnpi', 'providergroupnpi'],
  providerTaxid: ['providertaxid', 'taxid', 'providertin', 'tin'],
  city: ['city', 'patientcity'],
  state: ['state', 'patientstate'],
  postalCode: ['postalcode', 'zipcode', 'zip', 'postal', 'patientpostalcode'],
  facility: ['facility', 'facilityname'],
  Subscriber: ['patienttype', 'subscriber', 'subscribertype', 'relationship'],
  Subscriber_First_Name: ['subscriberfirstname', 'subscriberfname'],
  Subscriber_Last_Name: ['subscriberlastname', 'subscriberlname'],
  Subscriber_DOB: ['subscriberdob', 'subscriberdateofbirth'],
  Subscriber_Member_ID: ['subscribermemberid', 'subscriberid'],
  InsuranceNetwork: ['insurancenetwork', 'network'],
  FeeSchedule: ['feeschedule', 'fee'],
};

// Set of default field names that are supplied by the template and must be hidden.
const _buildHiddenDefaultFields = (headers: DynamicInputHeader[]): Set<string> => {
  const hidden = new Set<string>();
  if (!headers.length) return hidden;
  // Strip the "_2", "_3" … suffix added for repeated keys before matching.
  const dynamicTokens = new Set(
    headers.map((h) => _normalizeFieldName(h.key.replace(/_\d+$/, ''))),
  );
  Object.entries(DEFAULT_FIELD_ALIASES).forEach(([fieldName, aliases]) => {
    if (aliases.some((alias) => dynamicTokens.has(alias))) hidden.add(fieldName);
  });
  return hidden;
};

// Human readable label for a dynamic field ("postal_code" → "Postal Code").
const _dynamicFieldLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
// ─────────────────────────────────────────────────────────────────────────────

interface DeleteRowData {
    Patientname: string;
    id: string;
    PatientDOB: string;
    Memberid: string;
    client_name: string;
    uploaded_date: string;
    RefId: string;
    Payor_ID: string;
    Provider_NPI: string;
    template_mapping_time: string;
    xml_conversion_time: string;
    execution_time: string;
    Payer_ID: string;
    Provider_Name: string;
    Provider_ID: string;
    Payer_Name: string;
    Data_Fetched: string;
    xml_conversion: string;
    template_creation: string;
    error_message: string;
    error_reason: string;
    template: string;
    FeeSchedule: string;
    regen_status: string;
    UserName: string;
    Type: string;
    Payer_name: string;
    pdf_status: string;
    Provider_or_Company_name: string;
    InsuranceNetwork: string;
    // location: string;
    providerTaxid: string;
    city: string;
    state: string;
    postalCode: string;
    facility: string;
    Regenratestatus: string;
    DataMapping: string;
}

interface JsonData {
    field: string;
    value?: any;
    before?: any;
    after?: any;
}

interface AuditData {
    Modified: string;
    Action: string;
    JsonData: JsonData[];
    UserName: string;
}

interface IDefaultValues {
    DoB: string;
    firstName: string;
    MiddleName?: string;
    lastName: string;
    Memberid: string;
    // PayorID: string;
    ProviderNPI: string;
    ClientName: string;
    Insurancename: string;
    Subscriber: string;
    Address: string;
    FeeSchedule: string;
    Provider_or_Company_name: string;
    InsuranceNetwork: string;
    // location: string;
    providerTaxid: string;
    city: string;
    state: string;
    postalCode: string;
    facility: string;
    Subscriber_First_Name: string;
    Subscriber_Last_Name: string;
    Subscriber_DOB: string;
    Subscriber_Member_ID: string;
}

interface Iinsuranceoption {
    label: string;
    value: string;
    bot_status?: string;
    IsAPIEnabled?: boolean;
}

interface Addresseoption {
    label: string;
    value: string;
}

const defaultValues: IDefaultValues = {
    DoB: '',
    firstName: '',
    MiddleName: '',
    lastName: '',
    Memberid: '',
    // PayorID: '',
    ProviderNPI: '',
    ClientName: '',
    Insurancename: '',
    Subscriber: '',
    Address: '',
    FeeSchedule: "",
    Provider_or_Company_name: "",
    InsuranceNetwork: '',
    // location: '',
    providerTaxid: '',
    city: '',
    state: '',
    postalCode: '',
    facility: '',
    Subscriber_First_Name: "",
    Subscriber_Last_Name: "",
    Subscriber_DOB: "",
    Subscriber_Member_ID: "",
};

type PatientData = {
    First_Name: string;
    MiddleName: string;
    Last_Name: string;
    Memberid: string;
    Type: string;
    PatientDOB: string;
    Provider_NPI: string;
    Payer_name: string;
    RefId: string;
    Subscriber_First_Name: string;
    Subscriber_Last_Name: string;
    Subscriber_DOB: string;
    Subscriber_Member_ID: string;
};


interface IClientOption {
    label: string;
    value: string;
}

interface IOption {
    value: string;
    label: string;
}

interface FeeScheduleOption {
    value: string;
    label: string;
}


const SubscriberOptions: IOption[] = [
    { value: 'subscriber', label: 'Self' },
    { value: 'dependent', label: 'Dependent' },
];

const FeeScheduleOption: IOption[] = [
    { value: 'PPO', label: 'PPO' },
    { value: 'PREMIER', label: 'PREMIER' },
    { value: 'NONPARTICIPATING', label: 'NONPARTICIPATING' },
];

const CustomHeader = ({ handleFilter, value, handlePerPage, rowsPerPage, columnOptions, savedColumns, onColumnsChange }: any) => {
    return (
        <div className='client-master-list-table-header w-100 py-2'>
            <Row>
                <Col lg='2' className='d-flex align-items-center px-0 px-lg-1'>
                    <div className='d-flex align-items-center me-4 '>
                        <label htmlFor='rows-per-page' className='me-2'>Rows per Page:</label>
                        <Input
                            type='select'
                            id='rows-per-page'
                            value={rowsPerPage}
                            onChange={handlePerPage}
                            className='form-control ms-50 larger-select '
                        >
                            <option value='10'>10</option>
                            <option value='25'>25</option>
                            <option value='50'>50</option>
                        </Input>
                    </div>
                </Col>
                {/* Color Indicator Legend */}
                <Col lg='4' className='d-flex align-items-center px-0 px-lg-1'>
                    <div style={{
                        marginTop: '20px',
                        padding: '10px 2%',
                        display: 'flex',
                        gap: '30px',
                        alignItems: 'center',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'rgba(34, 223, 16, 1)',
                                borderRadius: '3px'
                            }}></div>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>Completed</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#f54552',
                                borderRadius: '3px'
                            }}></div>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>Error</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#800080',
                                borderRadius: '3px'
                            }}></div>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>In Progress</span>
                        </div>
                    </div>
                </Col>
                <Col
                    lg='6'
                    className='d-flex align-items-center justify-content-lg-end mt-lg-0 mt-1 px-0 px-lg-3'
                >
                    <div className="mb-2 me-2">
                        <ColumnChooser options={columnOptions} savedColumns={savedColumns} onChange={onColumnsChange} />
                    </div>
                    <InputGroup className="input-group-search mb-2 zoom-on-hover">
                        <Input
                            type="text"
                            placeholder="🔍 Search...."
                            value={value}
                            onChange={(e) => handleFilter(e.target.value.trim())} // Trim whitespace before filtering
                            className="input-search"
                        />
                    </InputGroup>

                </Col>
            </Row>
        </div>
    );
};

const Bulkupload: React.FC = () => {
    const { control, handleSubmit, formState: { errors }, reset } = useForm<IDefaultValues>({
        defaultValues,
    });


    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const [selectedClient, setSelectedClient] = useState<string>('');
    const [selectedtemplate, setselectedtemplate] = useState<string>('');
    const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>('');
    const [selectedtemplateId, setselectedtemplateId] = useState<string>('');
    const [SelectedInsurancename, setSelectedInsurance] = useState<string>('');
    const [SelectedInsuranceId, setSelectedInsuranceId] = useState<string>('');
    const [SelectedAddressname, setSelectedAddress] = useState<string>('');
    const [SelectedAddressId, setSelectedAddressId] = useState<string>('');
    const Username = localStorage.UserName;
    const [UserType] = useState<string>(localStorage.UserType);
    const [Id, setId] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isOpenpatient, setIsOpenpatient] = useState<boolean>(false);
    const [editOpen, setIsEditOpen] = useState<boolean>(false);
    const [editData, setIsEditData] = useState<PatientData | null>(null);
    const [editId, setEditId] = useState<string>('');
    const toggle = () => setIsOpen(!isOpen);
    const togglepatient = () => setIsOpenpatient(!isOpenpatient);
    const navigate = useNavigate();
    const [RestartId, setRestartId] = useState<string>('');
    const [scrollInnerModal, setScrollInnerModal] = useState<boolean>(false);
    const [filteredAuditData, setFilteredAuditData] = useState<any[]>([]);
    const [refid, setRefid] = useState<string | null>(null);
    const [formValues, setFormValues] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        memberId: "",
        patientType: "",
        dob: "",
        providerNpi: "",
        insurance: "",
        feeSchedule: "",
    });
    const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
    const [vendorbasedclientOptions, setvendorbasedclientOptions] = useState<IClientOption[]>([]);
    const [templateOptions, settemplateOptions] = useState<IClientOption[]>([]);
    const [insuranceoption, setInsurance] = useState<Iinsuranceoption[]>([]);
    const [Addresseoption, setAddress] = useState<[]>([]);
    const [matchedRpaTask, setMatchedRpaTask] = useState<BotManagerTask | null>(null);
    const [dynamicHeaders, setDynamicHeaders] = useState<DynamicInputHeader[]>([]);
    const [dynamicValues, setDynamicValues] = useState<Record<string, string>>({});
    const [dynamicErrors, setDynamicErrors] = useState<Record<string, string>>({});
    const [SelectedBotstatus, setSelectedBotstatus] = useState<string>('');
    const isDynamicRpaMode = dynamicHeaders.length > 0 && !!matchedRpaTask;
    // Default fields that the template already provides as dynamic fields.
    const hiddenDefaultFields = useMemo(
        () => (isDynamicRpaMode ? _buildHiddenDefaultFields(dynamicHeaders) : new Set<string>()),
        [isDynamicRpaMode, dynamicHeaders],
    );
    const isDefaultFieldHidden = useCallback(
        (fieldName: string) => hiddenDefaultFields.has(fieldName),
        [hiddenDefaultFields],
    );
    const [eventId, setEventId] = useState<DeleteRowData[]>([]);
    const [eventId1, setEventId1] = useState(0);
    const selectedRowsPerPage: { [key: number]: DeleteRowData[] } = useMemo(() => ({}), []);
    const menuActions = ['add', 'delete','restart'];
    const action = { fromUser: true };
    const dispatch = useAppDispatch()
    const store = useSelector((state: any) => state.ManualEntries)
    const [value, setValue] = useState<string>('');
    const [sort, setSort] = useState<string>('');
    const [sortColumn, setSortColumn] = useState<any>('uploaded_date'); // Example initial sort column
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [localData, setLocalData] = useState<DeleteRowData[]>(store.data);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [userId, setUserId] = useState<string>('');

    const [unitedHealthcareOtpId, setUnitedHealthcareOtpId] = useState<string>('');
    const [isUnitedHealthcareOTPModalOpen, setIsUnitedHealthcareOTPModalOpen] = useState<boolean>(false);
    const [unitedHealthcareOtp, setUnitedHealthcareOtp] = useState("");
    const [isUnitedHealthcareVerifying, setIsUnitedHealthcareVerifying] = useState(false);
    const [unitedHealthcareOtpError, setUnitedHealthcareOtpError] = useState("");

    const [KansasOtpId, setKansasOtpId] = useState<string>('');
    const [isKansasOTPModalOpen, setIsKansasOTPModalOpen] = useState<boolean>(false);
    const [kansasOtp, setKansasOtp] = useState("");
    const [iskansasOtpVerifying, setIskansasOtpVerifying] = useState<boolean>(false);
    const [kansasOtpError, setkansasOtpError] = useState<string>("");

    const [VirginiaOtpId, setVirginiaOtpId] = useState<string>('');
    const [isVirginiaOTPModalOpen, setIsVirginiaOTPModalOpen] = useState<boolean>(false);
    const [virginiaOtp, setVirginiaOtp] = useState("");
    const [isVirginiaVerifying, setIsVirginiaVerifying] = useState<boolean>(false);
    const [virginiaOtpError, setVirginiaOtpError] = useState<string>("");

    const [IowaOtpId, setIowaOtpId] = useState<string>('');
    const [isIowaOTPModalOpen, setIsIowaOTPModalOpen] = useState<boolean>(false);
    const [iowaOtp, setIowaOtp] = useState("");
    const [isIowaOtpVerifying, setIsIowaOtpVerifying] = useState<boolean>(false);
    const [IowaOtpError, setIowaOtpError] = useState<string>("");

    const [NortheastOtpId, setNortheastOtpId] = useState<string>('');
    const [isNortheastOTPModalOpen, setIsNortheastOTPModalOpen] = useState<boolean>(false);
    const [northeastOtp, setNortheastOtp] = useState("");
    const [isNortheastOtpVerifying, setIsNortheastOtpVerifying] = useState<boolean>(false);
    const [NortheastOtpError, setNortheastOtpError] = useState<string>("");

    const [GeorgiaOtpId, setGeorgiaOtpId] = useState<string>('');
    const [isGeorgiaOTPModalOpen, setIsGeorgiaOTPModalOpen] = useState<boolean>(false);
    const [georgiaOtp, setGeorgiaOtp] = useState("");
    const [isgeorgiaVerifying, setIsgeorgiaVerifying] = useState<boolean>(false);
    const [georgiaOtpError, setgeorgiaOtpError] = useState<string>("");

    const [GuardianOtpId, setGuardianOtpId] = useState<string>('');
    const [isGuardianOTPModalOpen, setIsGuardianOTPModalOpen] = useState<boolean>(false);
    const [guardianOtp, setGuardianOtp] = useState("");
    const [isguardianVerifying, setIsguardianVerifying] = useState<boolean>(false);
    const [guardianOtpError, setguardianOtpError] = useState<string>("");

    const [ArkansasOtpId, setArkansasOtpId] = useState<string>('');
    const [isArkansasOTPModalOpen, setIsArkansasOTPModalOpen] = useState<boolean>(false);
    const [arkansasOtp, setArkansasOtp] = useState("");
    const [isArkansasOtpVerifying, setIsArkansasOtpVerifying] = useState<boolean>(false);
    const [ArkansasOtpError, setArkansasOtpError] = useState<string>("");

    const [ColoradoOtpId, setColoradoOtpId] = useState<string>('');
    const [isColoradoOTPModalOpen, setIsColoradoOTPModalOpen] = useState<boolean>(false);
    const [coloradoOtp, setColoradoOtp] = useState("");
    const [isColoradoOtpVerifying, setIsColoradoOtpVerifying] = useState<boolean>(false);
    const [ColoradoOtpError, setColoradoOtpError] = useState<string>("");

    const [Individual_PlanOtpId, setIndividual_PlanOtpId] = useState<string>('');
    const [isIndividual_PlanOTPModalOpen, setIsIndividual_PlanOTPModalOpen] = useState<boolean>(false);
    const [individual_PlanOtp, setIndividual_PlanOtp] = useState("");
    const [isIndividual_PlanOtpVerifying, setIsIndividual_PlanOtpVerifying] = useState<boolean>(false);
    const [Individual_PlanOtpError, setIndividual_PlanOtpError] = useState<string>("");

    const [New_JerseyOtpId, setNew_JerseyOtpId] = useState<string>('');
    const [isNew_JerseyOTPModalOpen, setIsNew_JerseyOTPModalOpen] = useState<boolean>(false);
    const [new_JerseyOtp, setNew_JerseyOtp] = useState("");
    const [isNew_JerseyOtpVerifying, setIsNew_JerseyOtpVerifying] = useState<boolean>(false);
    const [New_JerseyOtpError, setNew_JerseyOtpError] = useState<string>("");

    const [New_YorkOtpId, setNew_YorkOtpId] = useState<string>('');
    const [isNew_YorkOTPModalOpen, setIsNew_YorkOTPModalOpen] = useState<boolean>(false);
    const [new_YorkOtp, setNew_YorkOtp] = useState("");
    const [isNew_YorkOtpVerifying, setIsNew_YorkOtpVerifying] = useState<boolean>(false);
    const [New_YorkOtpError, setNew_YorkOtpError] = useState<string>("");

    const [MassachusettsOtpId, setMassachusettsOtpId] = useState<string>('');
    const [isMassachusettsOTPModalOpen, setIsMassachusettsOTPModalOpen] = useState<boolean>(false);
    const [massachusettsOtp, setMassachusettsOtp] = useState("");
    const [isMassachusettsOtpVerifying, setIsMassachusettsOtpVerifying] = useState<boolean>(false);
    const [MassachusettsOtpError, setMassachusettsOtpError] = useState<string>("");

    const [WashingtonOtpId, setWashingtonOtpId] = useState<string>('');
    const [isWashingtonOTPModalOpen, setIsWashingtonOTPModalOpen] = useState<boolean>(false);
    const [washingtonOtp, setWashingtonOtp] = useState("");
    const [isWashingtonOtpVerifying, setIsWashingtonOtpVerifying] = useState<boolean>(false);
    const [WashingtonOtpError, setWashingtonOtpError] = useState<string>("");

    const [WisconsinOtpId, setWisconsinOtpId] = useState<string>('');
    const [isWisconsinOTPModalOpen, setIsWisconsinOTPModalOpen] = useState<boolean>(false);
    const [wisconsinOtp, setWisconsinOtp] = useState("");
    const [isWisconsinOtpVerifying, setIsWisconsinOtpVerifying] = useState<boolean>(false);
    const [WisconsinOtpError, setWisconsinOtpError] = useState<string>("");

    const [IllinoisOtpId, setIllinoisOtpId] = useState<string>('');
    const [isIllinoisOTPModalOpen, setIsIllinoisOTPModalOpen] = useState<boolean>(false);
    const [illinoisOtp, setIllinoisOtp] = useState("");
    const [isIllinoisOtpVerifying, setIsIllinoisOtpVerifying] = useState<boolean>(false);
    const [IllinoisOtpError, setIllinoisOtpError] = useState<string>("");

    const [RhodeIslandOtpId, setRhodeIslandOtpId] = useState<string>('');
    const [isRhodeIslandOTPModalOpen, setIsRhodeIslandOTPModalOpen] = useState<boolean>(false);
    const [rhodeIslandOtp, setRhodeIslandOtp] = useState("");
    const [isRhodeIslandOtpVerifying, setIsRhodeIslandOtpVerifying] = useState<boolean>(false);
    const [RhodeIslandOtpError, setRhodeIslandOtpError] = useState<string>("");

    const [OklahomaOtpId, setOklahomaOtpId] = useState<string>('');
    const [isOklahomaOTPModalOpen, setIsOklahomaOTPModalOpen] = useState<boolean>(false);
    const [oklahomaOtp, setOklahomaOtp] = useState("");
    const [isOklahomaVerifying, setIsOklahomaVerifying] = useState<boolean>(false);
    const [oklahomaOtpError, setOklahomaOtpError] = useState<string>("");

    const [AmertiasOtpId, setAmertiasOtpId] = useState<string>('');
    const [isAmertiasOTPModalOpen, setIsAmertiasOTPModalOpen] = useState<boolean>(false);
    const [amertiasOtp, setAmertiasOtp] = useState("");
    const [isAmertiasVerifying, setIsAmertiasVerifying] = useState<boolean>(false);
    const [AmertiasOtpError, setAmertiasOtpError] = useState<string>("");

    const [MissouriOtpId, setMissouriOtpId] = useState<string>('');
    const [isMissouriOTPModalOpen, setIsMissouriOTPModalOpen] = useState<boolean>(false);
    const [missouriOtp, setMissouriOtp] = useState("");
    const [isMissouriVerifying, setIsMissouriVerifying] = useState<boolean>(false);
    const [missouriOtpError, setMissouriOtpError] = useState<string>("");

    const [toolkitOtpId, setToolkitOtpId] = useState<string>('');
    const [isToolkitOTPModalOpen, setIsToolkitOTPModalOpen] = useState<boolean>(false);
    const [toolkitOtp, setToolkitOtp] = useState("");
    const [isToolkitVerifying, setIsToolkitVerifying] = useState<boolean>(false);
    const [toolkitOtpError, setToolkitOtpError] = useState<string>("");

    const [cignaOtpId, setCignaOtpId] = useState<string>('');
    const [isCignaOTPModalOpen, setIsCignaOTPModalOpen] = useState<boolean>(false);
    const [cignaOtp, setCignaOtp] = useState("");
    const [isCignaVerifying, setIsCignaVerifying] = useState<boolean>(false);
    const [cignaOtpError, setCignaOtpError] = useState<string>("");

    const [patientData, setPatientData] = useState<any>(null);
    const [viewingImage, setViewingImage] = useState<any>(false);
    const [Screenshot_status, setScreenshot_status] = useState("No");
    const [isDownloading, setIsDownloading] = useState(null);
    const User_Id = localStorage.getItem('id') || '';
    const LoginUser = localStorage.getItem('UserName') || '';
    const [loading, setLoading] = useState(false);
    const startIndex = (currentPage - 1) * rowsPerPage + 1;
    const endIndex = Math.min(currentPage * rowsPerPage, store.total);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({
            ...formValues,
            [e.target.name]: e.target.value,
        });
    };

    useEffect(() => {
        if (UserType !== 'MOS' && UserType !== 'Vendor') {
            setSelectedClient(localStorage.getItem('ClientName') || '');
            setSelectedClientId(localStorage.getItem('ClientId') || '');
        }
    }, [UserType]);
    useEffect(() => {
        if (UserType === 'Vendor') {
            setId(localStorage.getItem('VendorId') || '');
        } else
            if (UserType === 'Client') {
                setId(localStorage.getItem('ClientId') || '');
            }
            else if (UserType === 'MOS') {
                setId(localStorage.getItem('id') || '');
            }
    }, [UserType]);
    // Unified data fetching function with all parameters
    const fetchDataWithParams = useCallback(async (params: {
        q?: string;
        perPage?: number;
        page?: number;
        sort?: string;
        sortColumn?: string;
    } = {}) => {
        if (UserType && Id !== undefined) {
            const actionResult = await dispatch(
                getManualData({
                    Id,
                    UserType,
                    q: params.q !== undefined ? params.q : value,
                    perPage: params.perPage !== undefined ? params.perPage : rowsPerPage,
                    page: params.page !== undefined ? params.page : currentPage,
                    sort: params.sort !== undefined ? params.sort : sort,
                    sortColumn: params.sortColumn !== undefined ? params.sortColumn : sortColumn
                })
            );
            const payload = actionResult.payload as { data: DeleteRowData[] };
            setLocalData(payload.data);
        }
    }, [dispatch, UserType, Id, value, rowsPerPage, currentPage, sort, sortColumn]);

    // Fetch initial data only once on mount
    useEffect(() => {
        if (UserType && Id !== undefined) {
            fetchDataWithParams({});
        }
    }, [UserType, Id, fetchDataWithParams]);

    // Re-show OTP Loading Modal on Refresh, only for what is left of its 30 minutes
    useEffect(() => {
        const timeLeft = otpProcessingTimeLeft();
        if (timeLeft > 0) {
            const batchInsuranceName = localStorage.getItem('currentBatchInsuranceName') || "the patients";
            Swal.fire({
                title: 'Insurance Verification Platform',
                html: `
                    <div style="text-align: center;">
                        <span style="color: green; font-weight: bold; font-size: 1.2em;">Patients Added successfully!</span><br/><br/>
                        <span>OTP Authentication in progress for <b>${batchInsuranceName}</b>, please wait...</span>
                    </div>
                `,
                icon: 'success',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                timer: timeLeft,
                showClass: {
                    popup: 'animate__animated animate__flipInX',
                },
                didOpen: () => {
                    Swal.showLoading();
                }
            }).then(onOtpPopupClosed);
        }
    }, []);

    // Effect for handling WebSocket connection
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        // socket.onopen = () => console.log("WebSocket connected", socket);
        socket.onmessage = (event) => {
            const change = JSON.parse(event.data);
            // console.log('changes', change);

            // Update the local state with the real-time change
            setLocalData((prevData: DeleteRowData[]) =>
                prevData.map((item) => {
                    if (item.id === change.documentKey._id.$oid) {
                        return { ...item, ...change.updateDescription.updatedFields };
                    }
                    return item;
                })
            );
        };

        // Clean up: close the WebSocket connection when the component unmounts
        return () => {
            if (socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
        };
    }, [websocket_webservice]); // Add any additional dependencies if necessary


    const handleReloadTable = () => {
        dispatch(
            getManualData({
                Id,
                UserType,
                q: value,
                perPage: rowsPerPage,
                page: currentPage,
                sort,
                sortColumn
            })
        );
    };

    const handleFilter = (val: any) => {
        setValue(val);
        setCurrentPage(1);
        // Make single backend call with updated filter and reset page
        fetchDataWithParams({ q: val, page: 1 });
    }

    const handlePerPage = (e: any) => {
        const newPerPage = parseInt(e.target.value);
        setCurrentPage(1);
        setRowsPerPage(newPerPage);
        // Make single backend call with updated perPage and reset page
        fetchDataWithParams({ perPage: newPerPage, page: 1 });
    }

    const handlePagination = (page: any) => {
        const newPage = page.selected + 1;
        setCurrentPage(newPage);
        // Make single backend call with updated page
        fetchDataWithParams({ page: newPage });
    }

    const dataToRender = () => {
        const filters: any = {
            q: value
        };
        const isFiltered = Object.keys(filters).some(function (k) {
            return filters[k].length > 0;
        });

        if (localData.length > 0) {
            return localData;
        } else if (localData.length === 0 && isFiltered) {
            return [];
        } else {
            return store.allData.slice(0, rowsPerPage);
        }
    };

    const handleSort = (column: any, sortDirection: any) => {
        setSort(sortDirection);
        setSortColumn(column.sortField);
        // Make single backend call with updated sort parameters
        fetchDataWithParams({ sort: sortDirection, sortColumn: column.sortField });
    }

    const handleRestart = async (row: any) => {
        if (!row || typeof row !== "object") return;

        setRestartId(row.id);

        try {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, row.id);
            const response = await api.fetch("api/restart_patient_data", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

        } catch (error) {
            console.error("Error restarting patient data:", error);
        }
    };

    // United Healthcare OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_UnitedHealthcare_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("UNITED");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_UnitedHealthcare_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("UNITED");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setUnitedHealthcareOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setUnitedHealthcareOtp("");
                            setIsUnitedHealthcareVerifying(false);
                            setUnitedHealthcareOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsUnitedHealthcareOTPModalOpen(true);
                        }
                    }
                }

                // 3) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === unitedHealthcareOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsUnitedHealthcareVerifying(false);
                        setUnitedHealthcareOtp('');
                        setUnitedHealthcareOtpError("");
                        setIsUnitedHealthcareOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsUnitedHealthcareVerifying(false);
                        setUnitedHealthcareOtp('');
                        setUnitedHealthcareOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared

                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsUnitedHealthcareVerifying(false);
                        setIsUnitedHealthcareOTPModalOpen(false);

                        Swal.fire({
                            title: "Insurance Verification Platform",
                            text: "Time Out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, unitedHealthcareOtpId, userId]);

    // Kansas OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            // Trigger only when otp_status updates
            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Kansas_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("KANSAS");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Kansas_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((res) => res.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem('currentBatchInsuranceName') || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("KANSAS");
                        if (
                            (TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)
                        ) {
                            setKansasOtpId(TempData?.[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setKansasOtp('');
                            setIskansasOtpVerifying(false);
                            setkansasOtpError('');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsKansasOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Kansas
                if (receivedId && receivedId === KansasOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIskansasOtpVerifying(false);
                        setKansasOtp('');
                        setkansasOtpError('');
                        setIsKansasOTPModalOpen(false);

                        Swal.fire({
                            icon: 'success',
                            title: 'Insurance Verification Platform',
                            text: 'OTP verified successfully.',
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: 'btn btn-success' },
                            allowOutsideClick: false,
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP
                        setIskansasOtpVerifying(false);
                        setKansasOtp('');
                        setkansasOtpError('OTP incorrect. Please enter correct OTP.');
                    } else if (status === 'E') {
                        // Failed OTP validation
                        setIskansasOtpVerifying(false);
                        setIsKansasOTPModalOpen(false);
                        Swal.fire({
                            icon: 'error',
                            title: 'Insurance Verification Platform',
                            text: 'Time out',
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: 'btn btn-danger' },
                            allowOutsideClick: false,
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, KansasOtpId, userId]);

    // Virginia OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            // Trigger only when otp_status updates
            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Virginia_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("VIRGINIA");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_Virginia_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("VIRGINIA");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setVirginiaOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setVirginiaOtp("");
                            setIsVirginiaVerifying(false);
                            setVirginiaOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsVirginiaOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Virginia
                if (receivedId && receivedId === VirginiaOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsVirginiaVerifying(false);
                        setVirginiaOtp('');
                        setVirginiaOtpError("");
                        setIsVirginiaOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP
                        setIsVirginiaVerifying(false);
                        setVirginiaOtp('');
                        setVirginiaOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        // Failed OTP validation
                        setIsVirginiaVerifying(false);
                        setIsVirginiaOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, VirginiaOtpId, userId]);

    // Iowa OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            // Trigger only when otp_status updates
            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Iowa_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("IOWA");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_Iowa_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("IOWA");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setIowaOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setIowaOtp("");
                            setIsIowaOtpVerifying(false);
                            setIowaOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsIowaOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Iowa
                if (receivedId && receivedId === IowaOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsIowaOtpVerifying(false);
                        setIowaOtp('');
                        setIowaOtpError("");
                        setIsIowaOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP
                        setIsIowaOtpVerifying(false);
                        setIowaOtp('');
                        setIowaOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        // Failed OTP validation
                        setIsIowaOtpVerifying(false);
                        setIsIowaOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, IowaOtpId, userId]);

    // Northeast OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            // Trigger only when otp_status updates
            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Northeast_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("NORTHEAST");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_Northeast_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("NORTHEAST");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setNortheastOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setNortheastOtp("");
                            setIsNortheastOtpVerifying(false);
                            setNortheastOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsNortheastOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Northeast
                if (receivedId && receivedId === NortheastOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsNortheastOtpVerifying(false);
                        setNortheastOtp('');
                        setNortheastOtpError("");
                        setIsNortheastOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        setIsNortheastOtpVerifying(false);
                        setNortheastOtp('');
                        setNortheastOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsNortheastOtpVerifying(false);
                        setIsNortheastOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, NortheastOtpId, userId]);

    // Georgia OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {

            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            // Trigger only when otp_status updates
            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Georgia_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("GEORGIA");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_Georgia_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("GEORGIA");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setGeorgiaOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setGeorgiaOtp("");
                            setIsgeorgiaVerifying(false);
                            setgeorgiaOtpError("");
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsGeorgiaOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Oklahoma
                if (receivedId && receivedId === GeorgiaOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsgeorgiaVerifying(false);
                        setGeorgiaOtp('');
                        setgeorgiaOtpError("");
                        setIsGeorgiaOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP
                        setIsgeorgiaVerifying(false);
                        setGeorgiaOtp('');
                        setgeorgiaOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E' || updatedFields.Data_Fetched === 'E') {
                        // Failed OTP validation
                        setIsgeorgiaVerifying(false);
                        setIsGeorgiaOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, GeorgiaOtpId, userId]);

    // Guardian OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Guardian_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("GUARDIAN");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Guardian_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("GUARDIAN");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setGuardianOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setGuardianOtp("");
                            setIsguardianVerifying(false);
                            setguardianOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsGuardianOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === GuardianOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsguardianVerifying(false);
                        setGuardianOtp('');
                        setguardianOtpError("");
                        setIsGuardianOTPModalOpen(false);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsguardianVerifying(false);
                        setGuardianOtp('');
                        setguardianOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared
                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsguardianVerifying(false);
                        setIsGuardianOTPModalOpen(false);
                        setGuardianOtp('');
                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                        // Modal stays open, user can re-enter
                    }
                }

            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, GuardianOtpId, userId]);

    // Arkansas OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Arkansas_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("ARKANSAS");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N'
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_Arkansas_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("ARKANSAS");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setArkansasOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setArkansasOtp("");
                            setIsArkansasOtpVerifying(false);
                            setArkansasOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsArkansasOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Arkansas
                if (receivedId && receivedId === ArkansasOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsArkansasOtpVerifying(false);
                        setArkansasOtp('');
                        setArkansasOtpError("");
                        setIsArkansasOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        setIsArkansasOtpVerifying(false);
                        setArkansasOtp('');
                        setArkansasOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsArkansasOtpVerifying(false);
                        setIsArkansasOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, ArkansasOtpId, userId]);

    // Colorado OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Colorado_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("COLORADO");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N'
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_Colorado_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((r) => r.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("COLORADO");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setColoradoOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setColoradoOtp("");
                            setIsColoradoOtpVerifying(false);
                            setColoradoOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsColoradoOTPModalOpen(true);
                        }
                    }
                }

                // 2) Handle other statuses for Colorado
                if (receivedId && receivedId === ColoradoOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsColoradoOtpVerifying(false);
                        setColoradoOtp('');
                        setColoradoOtpError("");
                        setIsColoradoOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false,
                        });

                    } else if (status === 'R') {
                        setIsColoradoOtpVerifying(false);
                        setColoradoOtp('');
                        setColoradoOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsColoradoOtpVerifying(false);
                        setIsColoradoOTPModalOpen(false);

                        Swal.fire({
                            // //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false,
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, ColoradoOtpId, userId]);

    // Individual Plan OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_IndividualPlan_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("INDIVIDUALPLAN");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N'
                if (status === 'N') {
                    if (receivedId) {
                        // Call API to fetch OTP details
                        const key = await keyPromise
                        const EncParams = await AesEncrypt(key, User_Id);

                        const response = await api.fetch('api/mfa_otp/get_IndividualPlan_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);
                        // Match OTP record with logged-in user
                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("INDIVIDUALPLAN");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setIndividual_PlanOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setIndividual_PlanOtp('');
                            setIsIndividual_PlanOtpVerifying(false);
                            setIndividual_PlanOtpError('');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsIndividual_PlanOTPModalOpen(true); // 🔔 Open popup
                        }
                    }
                }
                // 2) Handle other statuses for Colorado
                if (receivedId && receivedId === Individual_PlanOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsIndividual_PlanOtpVerifying(false);
                        setIndividual_PlanOtp('');
                        setIndividual_PlanOtpError("");
                        setIsIndividual_PlanOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false,
                        });

                    } else if (status === 'R') {
                        setIsIndividual_PlanOtpVerifying(false);
                        setIndividual_PlanOtp('');
                        setIndividual_PlanOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsIndividual_PlanOtpVerifying(false);
                        setIsIndividual_PlanOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false,
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, Individual_PlanOtpId, userId]);

    // New Jersey OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA' → close the validation loading popup automatically
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_NewJersey_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("NEWJERSEY");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close(); // Auto-close the validation loading popup
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N'
                if (status === 'N') {
                    if (receivedId) {
                        // Call API to fetch OTP details
                        const key = await keyPromise
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_NewJersey_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);
                        // Match OTP record with logged-in user
                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("NEWJERSEY");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setNew_JerseyOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setNew_JerseyOtp('');
                            setIsNew_JerseyOtpVerifying(false);
                            setNew_JerseyOtpError('')
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsNew_JerseyOTPModalOpen(true); // 🔔 Open popup
                        }
                    }
                }
                // 3) Handle other statuses for New Jersey
                if (receivedId && receivedId === New_JerseyOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsNew_JerseyOtpVerifying(false);
                        setNew_JerseyOtp('');
                        setNew_JerseyOtpError("");
                        setIsNew_JerseyOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false,
                        });

                    } else if (status === 'R') {
                        setIsNew_JerseyOtpVerifying(false);
                        setNew_JerseyOtp('');
                        setNew_JerseyOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsNew_JerseyOtpVerifying(false);
                        setIsNew_JerseyOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false,
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, New_JerseyOtpId, userId]);

    // New York OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_NewYork_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("NEWYORK");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N'
                if (status === 'N') {
                    if (receivedId) {
                        // Call API to fetch OTP details
                        const key = await keyPromise
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_NewYork_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);
                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("NEWYORK");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setNew_YorkOtpId(TempData?.[0]?.id);
                            setNew_YorkOtp('');
                            setIsNew_YorkOtpVerifying(false);
                            setNew_YorkOtpError('')
                            Swal.close();
                            setIsNew_YorkOTPModalOpen(true); // 🔔 Open popup
                        }
                    }
                }
                // 2) When status becomes 'C', 'R', or 'E'
                if (receivedId && receivedId === New_YorkOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsNew_YorkOtpVerifying(false);
                        setNew_YorkOtp('');
                        setNew_YorkOtpError("");
                        setIsNew_YorkOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false,
                        });

                    } else if (status === 'R') {
                        setIsNew_YorkOtpVerifying(false);
                        setNew_YorkOtp('');
                        setNew_YorkOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsNew_YorkOtpVerifying(false);
                        setIsNew_YorkOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time Out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false,
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, New_YorkOtpId, userId]);

    // Massachusetts OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Massachusetts_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("MASSACHUSETTS");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N'
                if (status === 'N') {
                    if (receivedId) {
                        // Call API to fetch OTP details
                        const key = await keyPromise
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Massachusetts_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);
                        // Match OTP record with logged-in user
                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("MASSACHUSETTS");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setMassachusettsOtpId(TempData?.[0]?.id);
                            setUserId(TempData?.[0]?.Uploaded_userid);
                            setMassachusettsOtp('');
                            setIsMassachusettsOtpVerifying(false);
                            setMassachusettsOtpError('');
                            Swal.close();
                            setIsMassachusettsOTPModalOpen(true); // 🔔 Open popup
                        }
                    }
                }
                // 2) Handle other statuses for Colorado
                if (receivedId && receivedId === MassachusettsOtpId && userId === User_Id) {
                    if (status === 'C') {
                        setIsMassachusettsOtpVerifying(false);
                        setMassachusettsOtp('');
                        setMassachusettsOtpError("");
                        setIsMassachusettsOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-success" },
                            allowOutsideClick: false,
                        });

                    } else if (status === 'R') {
                        setIsMassachusettsOtpVerifying(false);
                        setMassachusettsOtp('');
                        setMassachusettsOtpError("OTP incorrect. Please enter correct OTP.");

                    } else if (status === 'E') {
                        setIsMassachusettsOtpVerifying(false);
                        setIsMassachusettsOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: { confirmButton: "btn btn-danger" },
                            allowOutsideClick: false,
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, MassachusettsOtpId, userId]);

    // Washington OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Washington_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("WASHINGTON");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Washington_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("WASHINGTON");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setWashingtonOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setWashingtonOtp("");
                            setIsWashingtonOtpVerifying(false);
                            setWashingtonOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsWashingtonOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === WashingtonOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsWashingtonOtpVerifying(false);
                        setWashingtonOtp('');
                        setWashingtonOtpError("");
                        setIsWashingtonOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsWashingtonOtpVerifying(false);
                        setWashingtonOtp('');
                        setWashingtonOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared

                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsWashingtonOtpVerifying(false);
                        setIsWashingtonOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, WashingtonOtpId, userId]);

    // Wisconsin OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Wisconsin_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("WISCONSIN");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Wisconsin_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("WISCONSIN");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setWisconsinOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setWisconsinOtp("");
                            setIsWisconsinOtpVerifying(false);
                            setWisconsinOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsWisconsinOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === WisconsinOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsWisconsinOtpVerifying(false);
                        setWisconsinOtp('');
                        setWisconsinOtpError("");
                        setIsWisconsinOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsWisconsinOtpVerifying(false);
                        setWisconsinOtp('');
                        setWisconsinOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared

                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsWisconsinOtpVerifying(false);
                        setIsWisconsinOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, WisconsinOtpId, userId]);

    // Illinois OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Illinois_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("ILLINOIS");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 1) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Illinois_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("ILLINOIS");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setIllinoisOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setIllinoisOtp("");
                            setIsIllinoisOtpVerifying(false);
                            setIllinoisOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsIllinoisOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === IllinoisOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsIllinoisOtpVerifying(false);
                        setIllinoisOtp('');
                        setIllinoisOtpError("");
                        setIsIllinoisOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsIllinoisOtpVerifying(false);
                        setIllinoisOtp('');
                        setIllinoisOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared

                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsIllinoisOtpVerifying(false);
                        setIsIllinoisOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, IllinoisOtpId, userId]);

    // Rhode Island OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_RhodeIsland_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("RHODEISLAND");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_RhodeIsland_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());

                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("RHODEISLAND");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setRhodeIslandOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setRhodeIslandOtp("");
                            setIsRhodeIslandOtpVerifying(false);
                            setRhodeIslandOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsRhodeIslandOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === RhodeIslandOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsRhodeIslandOtpVerifying(false);
                        setRhodeIslandOtp('');
                        setRhodeIslandOtpError("");
                        setIsRhodeIslandOTPModalOpen(false);

                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });

                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsRhodeIslandOtpVerifying(false);
                        setRhodeIslandOtp('');
                        setRhodeIslandOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared

                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsRhodeIslandOtpVerifying(false);
                        setIsRhodeIslandOTPModalOpen(false);

                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, RhodeIslandOtpId, userId]);

    // Oklahoma OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);

        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (
                change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Oklahoma_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("OKLAHOMA");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 1) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Oklahoma_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("OKLAHOMA");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setOklahomaOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setOklahomaOtp("");
                            setIsOklahomaVerifying(false);
                            setOklahomaOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsOklahomaOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === OklahomaOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsOklahomaVerifying(false);
                        setOklahomaOtp('');
                        setOklahomaOtpError("");
                        setIsOklahomaOTPModalOpen(false);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsOklahomaVerifying(false);
                        setOklahomaOtp('');
                        setOklahomaOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared
                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsOklahomaVerifying(false);
                        setIsOklahomaOTPModalOpen(false);
                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                        // Modal stays open, user can re-enter
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, OklahomaOtpId, userId]);

    // Amertias OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (
                change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Amertias_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("AMERTIAS");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_Amertias_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("AMERTIAS");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setAmertiasOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setAmertiasOtp("");
                            setIsAmertiasVerifying(false);
                            setAmertiasOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsAmertiasOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === AmertiasOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsAmertiasVerifying(false);
                        setAmertiasOtp('');
                        setAmertiasOtpError("");
                        setIsAmertiasOTPModalOpen(false);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsAmertiasVerifying(false);
                        setAmertiasOtp('');
                        setAmertiasOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared
                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsAmertiasVerifying(false);
                        setIsAmertiasOTPModalOpen(false);
                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time Out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                        // Modal stays open, user can re-enter
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, AmertiasOtpId, userId]);

    // Missouri OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (
                change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_missouri_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("MISSOURI");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_missouri_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("MISSOURI");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setMissouriOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setMissouriOtp("");
                            setIsMissouriVerifying(false);
                            setMissouriOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsMissouriOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === MissouriOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsMissouriVerifying(false);
                        setMissouriOtp('');
                        setMissouriOtpError("");
                        setIsMissouriOTPModalOpen(false);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsMissouriVerifying(false);
                        setMissouriOtp('');
                        setMissouriOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared
                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsMissouriVerifying(false);
                        setIsMissouriOTPModalOpen(false);
                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time Out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                        // Modal stays open, user can re-enter
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, MissouriOtpId, userId]);

    // Toolkit OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (
                change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_toolkit_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("TOOLKIT");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_toolkit_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("TOOLKIT");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setToolkitOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setToolkitOtp("");
                            setIsToolkitVerifying(false);
                            setToolkitOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsToolkitOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === toolkitOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsToolkitVerifying(false);
                        setToolkitOtp('');
                        setToolkitOtpError("");
                        setIsToolkitOTPModalOpen(false);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsToolkitVerifying(false);
                        setToolkitOtp('');
                        setToolkitOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared
                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsToolkitVerifying(false);
                        setIsToolkitOTPModalOpen(false);
                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time Out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                        // Modal stays open, user can re-enter
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, toolkitOtpId, userId]);

    // Cigna OTP Process
    useEffect(() => {
        const socket = new WebSocket(`${websocket_webservice}ws/BulkuploadPatient/`);
        socket.onmessage = async (event) => {
            const change = JSON.parse(event.data);
            const updatedFields = change.updateDescription?.updatedFields || {};
            const receivedId = change.documentKey?._id?.$oid;

            if (
                change.operationType === 'update' && 'otp_status' in updatedFields) {
                const status = updatedFields.otp_status;

                // 1) When status becomes 'NA'
                if (status === 'NA' && localStorage.getItem('isBulkUploadProcessing') === 'true') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_cigna_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("CIGNA");
                        if ((TempData.length > 0 && TempData[0] && TempData?.[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            const insuranceName = (TempData.length > 0 ? TempData?.[0]?.Payer_name : batchInsurance) || "the patients";
                            localStorage.removeItem('isBulkUploadProcessing');
                            localStorage.removeItem('currentBatchPatientIds');
                            localStorage.removeItem('currentBatchInsuranceName');
                            Swal.close();
                            Swal.fire({
                                icon: "success",
                                title: "Insurance Verification Platform",
                                text: `Authentication complete for ${insuranceName}.`,
                                timer: 3000,
                                allowOutsideClick: false,
                            });
                        }
                    }
                }

                // 2) When status becomes 'N' → open modal for this user
                if (status === 'N') {
                    if (receivedId) {
                        const key = await keyPromise;
                        const EncParams = await AesEncrypt(key, User_Id);
                        const response = await api.fetch('api/mfa_otp/get_cigna_OTP', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams })
                        }).then((response) => response.json());
                        const TempData = await AesDecrypt(key, response);

                        const currentBatchIds = JSON.parse(localStorage.getItem('currentBatchPatientIds') || '[]');
                        const batchInsurance = (localStorage.getItem("currentBatchInsuranceName") || "").toUpperCase();
                        const isMyInsurer = batchInsurance.includes("CIGNA");
                        if ((TempData?.[0]?.id && TempData[0]?.Uploaded_userid === User_Id) || (receivedId && currentBatchIds.includes(receivedId) && isMyInsurer)) {
                            setCignaOtpId(TempData[0]?.id || receivedId);
                            setUserId(TempData[0]?.Uploaded_userid || User_Id);
                            setCignaOtp("");
                            setIsCignaVerifying(false);
                            setCignaOtpError("");
                            localStorage.removeItem('isBulkUploadProcessing');
                            Swal.close();
                            setIsCignaOTPModalOpen(true); // open popup
                        }
                    }
                }

                // 2) All other statuses apply for the current OTP record of this user
                if (receivedId && receivedId === cignaOtpId && userId === User_Id) {
                    if (status === 'C') {
                        // OTP verified successfully
                        setIsCignaVerifying(false);
                        setCignaOtp('');
                        setCignaOtpError("");
                        setIsCignaOTPModalOpen(false);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "OTP verified successfully.",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                    } else if (status === 'R') {
                        // Incorrect OTP → allow re-entry
                        setIsCignaVerifying(false);
                        setCignaOtp('');
                        setCignaOtpError("OTP incorrect. Please enter correct OTP.");
                        // Modal stays open, input cleared
                    } else if (status === 'E') {
                        // Failed to validate OTP
                        setIsCignaVerifying(false);
                        setIsCignaOTPModalOpen(false);
                        Swal.fire({
                            //icon: "error",
                            title: "Insurance Verification Platform",
                            text: "Time Out",
                            showConfirmButton: true,
                            timer: 3000,
                            customClass: {
                                confirmButton: "btn btn-danger",
                            },
                            allowOutsideClick: false
                        });
                        // Modal stays open, user can re-enter
                    }
                }
            }
        };

        return () => {
            socket.close();
        };
    }, [User_Id, cignaOtpId, userId]);

    const CustomPagination = () => {
        const count = Number(Math.ceil(store.total / rowsPerPage))

        return (
            <ReactPaginate
                previousLabel={<ChevronLeft size={16} />}
                nextLabel={<ChevronRight size={16} />}
                breakLabel="..."
                pageCount={count || 1}
                onPageChange={handlePagination}
                forcePage={currentPage !== 0 ? currentPage - 1 : 0}
                containerClassName="pagination-container"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakClassName="page-item"
                breakLinkClassName="page-link"
                activeClassName="active"
            />
        )
    }


    //United Healthcare OTP Process start
    const UnitedHealthcareOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_UnitedHealthcare_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchUnitedHealthcareOTP = async () => {
            const response = await UnitedHealthcareOtp();
            if (response.length > 0 && response[0].id) {
                setUnitedHealthcareOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchUnitedHealthcareOTP();
    }, []);

    useEffect(() => {
        if (unitedHealthcareOtpId && userId === User_Id) {
            setIsUnitedHealthcareOTPModalOpen(true);
        }
    }, [unitedHealthcareOtpId]);

    const handleSubmitUnitedHealthcareOtp = async () => {
        const Otpdata = { Id: unitedHealthcareOtpId, unitedHealthcare_code: unitedHealthcareOtp, LoginUser: LoginUser };
        try {
            setIsUnitedHealthcareVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch('api/mfa_otp/Update_UnitedHealthcare_OTP', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            // If API says failure, stop verifying and show error
            if (responseData !== 'OTP Updated Successfully') {
                setIsUnitedHealthcareVerifying(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Insurance Verification Platform',
                    text: 'Failed to send OTP for verification. Please try again.',
                    showConfirmButton: true,
                    customClass: { confirmButton: 'btn btn-danger' },
                    allowOutsideClick: false,
                });
            }

            // On success, keep modal open; WebSocket will react to otp_status (C/R/E)
        } catch (error) {
            console.error('Error submitting OTP: ', error);
            setIsUnitedHealthcareVerifying(false);
            Swal.fire({
                icon: 'error',
                title: 'Insurance Verification Platform',
                text: 'An error occurred while submitting OTP.',
                showConfirmButton: true,
                customClass: { confirmButton: 'btn btn-danger' },
                allowOutsideClick: false,
            });
        }
    };
    //United Healthcare OTP Process end

    //Kansas OTP Process start
    const KansasOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Kansas_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchKansasOTP = async () => {
            const response = await KansasOtp();
            if (response.length > 0 && response[0].id) {
                setKansasOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchKansasOTP();
    }, []);

    useEffect(() => {
        if (KansasOtpId && userId === User_Id) {
            setIsKansasOTPModalOpen(true);
        }
    }, [KansasOtpId]);

    const handleSubmitkansasOtp = async () => {
        const Otpdata = { Id: KansasOtpId, kansas_code: kansasOtp, LoginUser: LoginUser };
        try {
            setIskansasOtpVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/UpdateKansas_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIskansasOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIskansasOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Kansas OTP Process end

    //Virginia OTP Process start
    const VirginiaOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Virginia_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);

        return TempData;
    };

    useEffect(() => {
        const fetchVirginiaOTP = async () => {
            const response = await VirginiaOtp();
            if (response.length > 0 && response[0].id) {
                setVirginiaOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchVirginiaOTP();
    }, []);

    useEffect(() => {
        if (VirginiaOtpId && userId === User_Id) {
            setIsVirginiaOTPModalOpen(true);
        }
    }, [VirginiaOtpId]);

    const handleSubmitVirginiaOtp = async () => {
        const Otpdata = { Id: VirginiaOtpId, virginia_code: virginiaOtp, LoginUser: LoginUser };
        try {
            setIsVirginiaVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/UpdateVirginia_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsVirginiaVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // Keep modal open; WebSocket will close it on C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsVirginiaVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Virginia OTP Process end

    //Iowa OTP Process start
    const IowaOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Iowa_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchIowaOTP = async () => {
            const response = await IowaOtp();
            if (response.length > 0 && response[0].id) {
                setIowaOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchIowaOTP();
    }, []);

    useEffect(() => {
        if (IowaOtpId && userId === User_Id) {
            setIsIowaOTPModalOpen(true);
        }
    }, [IowaOtpId]);

    const handleSubmitIowaOtp = async () => {
        const Otpdata = { Id: IowaOtpId, Iowa_code: iowaOtp, LoginUser: LoginUser };
        try {
            setIsIowaOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/UpdateIowa_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsIowaOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }
            // Keep modal open; WebSocket will close it on C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsIowaOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Iowa OTP Process end

    //Northeast OTP Process start
    const NortheastOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Northeast_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchNortheastOTP = async () => {
            const response = await NortheastOtp();
            if (response.length > 0 && response[0].id) {
                setNortheastOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchNortheastOTP();
    }, []);

    useEffect(() => {
        if (NortheastOtpId && userId === User_Id) {
            setIsNortheastOTPModalOpen(true);
        }
    }, [NortheastOtpId]);

    const handleSubmitNortheastOtp = async () => {
        const Otpdata = { Id: NortheastOtpId, northeast_code: northeastOtp, LoginUser: LoginUser };
        try {
            setIsNortheastOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/UpdateNortheast_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsNortheastOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }
            // Modal stays open until WebSocket status C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsNortheastOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Northeast OTP Process end


    //Georgia OTP Process start
    const GeorgiaOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Georgia_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchGeorgiaOTP = async () => {
            const response = await GeorgiaOtp();
            if (response.length > 0 && response[0].id) {
                setGeorgiaOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchGeorgiaOTP();
    }, []);

    useEffect(() => {
        if (GeorgiaOtpId && userId === User_Id) {
            setIsGeorgiaOTPModalOpen(true);
        }
    }, [GeorgiaOtpId]);

    const handleSubmitGeorgiaOtp = async () => {
        const Otpdata = { Id: GeorgiaOtpId, georgia_code: georgiaOtp, LoginUser: LoginUser };
        try {
            setIsgeorgiaVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/UpdateGeorgia_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsgeorgiaVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsgeorgiaVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Georgia OTP Process end

    //Guardian OTP Process start
    const GuardianOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Guardian_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchGuardianOTP = async () => {
            const response = await GuardianOtp();
            if (response.length > 0 && response[0].id) {
                setGuardianOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchGuardianOTP();
    }, []);

    useEffect(() => {
        if (GuardianOtpId && userId === User_Id) {
            setIsGuardianOTPModalOpen(true);
        }
    }, [GuardianOtpId]);

    const handleSubmitGuardianOtp = async () => {
        const Otpdata = { Id: GuardianOtpId, guardian_code: guardianOtp, LoginUser: LoginUser };
        try {
            setIsguardianVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/UpdateGuardian_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsguardianVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsguardianVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Guardian OTP Process end

    //Arkansas OTP Process start
    const ArkansasOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Arkansas_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchArkansasOTP = async () => {
            const response = await ArkansasOtp();
            if (response.length > 0 && response[0].id) {
                setArkansasOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchArkansasOTP();
    }, []);

    useEffect(() => {
        if (ArkansasOtpId && userId === User_Id) {
            setIsArkansasOTPModalOpen(true);
        }
    }, [ArkansasOtpId]);

    const handleSubmitArkansasOtp = async () => {
        const Otpdata = { Id: ArkansasOtpId, arkansas_code: arkansasOtp, LoginUser: LoginUser };
        try {
            setIsArkansasOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/UpdateArkansas_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsArkansasOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }
            // Keep modal open; WebSocket will close it on C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsArkansasOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Arkansas OTP Process end

    //Colorado OTP Process start
    const ColoradoOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Colorado_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchColoradoOTP = async () => {
            const response = await ColoradoOtp();
            if (response.length > 0 && response[0].id) {
                setColoradoOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchColoradoOTP();
    }, []);

    useEffect(() => {
        if (ColoradoOtpId && userId === User_Id) {
            setIsColoradoOTPModalOpen(true);
        }
    }, [ColoradoOtpId]);

    const handleSubmitColoradoOtp = async () => {
        const Otpdata = { Id: ColoradoOtpId, colorado_code: coloradoOtp, LoginUser: LoginUser };
        try {
            setIsColoradoOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/Update_Colorado_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();


            if (responseData !== "OTP Updated Successfully") {
                setIsColoradoOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: { confirmButton: "btn btn-danger" },
                    allowOutsideClick: false,
                });
            }
            // Modal stays open; WebSocket handles C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsColoradoOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: { confirmButton: "btn btn-danger" },
                allowOutsideClick: false,
            });
        }
    };
    //Colorado OTP Process end

    //Individual Plan OTP Process start
    const Individual_PlanOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_IndividualPlan_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchIndividual_PlanOTP = async () => {
            const response = await Individual_PlanOtp();
            if (response.length > 0 && response[0].id) {
                setIndividual_PlanOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchIndividual_PlanOTP();
    }, []);

    useEffect(() => {
        if (Individual_PlanOtpId && userId === User_Id) {
            setIsIndividual_PlanOTPModalOpen(true);
        }
    }, [Individual_PlanOtpId]);

    const handleSubmitIndividual_PlanOtp = async () => {
        const Otpdata = { Id: Individual_PlanOtpId, individual_Plan_code: individual_PlanOtp, LoginUser: LoginUser };
        try {
            setIsIndividual_PlanOtpVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/Update_IndividualPlan_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();
            if (responseData !== "OTP Updated Successfully") {
                setIsIndividual_PlanOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: { confirmButton: "btn btn-danger" },
                    allowOutsideClick: false,
                });
            }
            // Modal stays open; WebSocket handles C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsIndividual_PlanOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: { confirmButton: "btn btn-danger" },
                allowOutsideClick: false,
            });
        }
    };
    //Individual Plan OTP Process end

    //New Jersey OTP Process start
    const New_JerseyOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_NewJersey_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchNew_JerseyOTP = async () => {
            const response = await New_JerseyOtp();
            if (response.length > 0 && response[0].id) {
                setNew_JerseyOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchNew_JerseyOTP();
    }, []);

    useEffect(() => {
        if (New_JerseyOtpId && userId === User_Id) {
            setIsNew_JerseyOTPModalOpen(true);
        }
    }, [New_JerseyOtpId]);

    const handleSubmitNew_JerseyOtp = async () => {
        const Otpdata = { Id: New_JerseyOtpId, New_Jersey_code: new_JerseyOtp, LoginUser: LoginUser };
        try {
            setIsNew_JerseyOtpVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/Update_NewJersey_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();
            if (responseData !== "OTP Updated Successfully") {
                setIsNew_JerseyOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: { confirmButton: "btn btn-danger" },
                    allowOutsideClick: false,
                });
            }
            // Modal stays open; WebSocket handles C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsNew_JerseyOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: { confirmButton: "btn btn-danger" },
                allowOutsideClick: false,
            });
        }
    };
    //New Jersey OTP Process end

    //New York OTP Process start
    const New_YorkOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_NewYork_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchNew_YorkOTP = async () => {
            const response = await New_YorkOtp();
            if (response.length > 0 && response[0].id) {
                setNew_YorkOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchNew_YorkOTP();
    }, []);

    useEffect(() => {
        if (New_YorkOtpId && userId === User_Id) {
            setIsNew_YorkOTPModalOpen(true);
        }
    }, [New_YorkOtpId]);

    const handleSubmitNew_YorkOtp = async () => {
        const Otpdata = { Id: New_YorkOtpId, New_York_code: new_YorkOtp, LoginUser: LoginUser };
        try {
            setIsNew_YorkOtpVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/Update_NewYork_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();
            if (responseData !== "OTP Updated Successfully") {
                setIsNew_YorkOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: { confirmButton: "btn btn-danger" },
                    allowOutsideClick: false,
                });
            }
            // Modal stays open; WebSocket handles C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsNew_YorkOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: { confirmButton: "btn btn-danger" },
                allowOutsideClick: false,
            });
        }
    };
    //New York OTP Process end

    //Massachusetts OTP Process start
    const MassachusettsOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Massachusetts_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchMassachusettsOTP = async () => {
            const response = await MassachusettsOtp();
            if (response.length > 0 && response[0].id) {
                setMassachusettsOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchMassachusettsOTP();
    }, []);

    useEffect(() => {
        if (MassachusettsOtpId && userId === User_Id) {
            setIsMassachusettsOTPModalOpen(true);
        }
    }, [MassachusettsOtpId]);

    const handleSubmitMassachusettsOtp = async () => {
        const Otpdata = { Id: MassachusettsOtpId, massachusetts_code: massachusettsOtp, LoginUser: LoginUser };
        try {
            setIsMassachusettsOtpVerifying(true);
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, Otpdata); // Convert object to string before encryption
            const response = await api.fetch("api/mfa_otp/Update_Massachusetts_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });
            const responseData = await response.json();
            if (responseData !== "OTP Updated Successfully") {
                setIsMassachusettsOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: { confirmButton: "btn btn-danger" },
                    allowOutsideClick: false,
                });
            }
            // Modal stays open; WebSocket handles C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsMassachusettsOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: { confirmButton: "btn btn-danger" },
                allowOutsideClick: false,
            });
        }
    };
    //Massachusetts OTP Process end

    //Washington OTP Process start
    const WashingtonOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Washington_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchWashingtonOTP = async () => {
            const response = await WashingtonOtp();
            if (response.length > 0 && response[0].id) {
                setWashingtonOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchWashingtonOTP();
    }, []);

    useEffect(() => {
        if (WashingtonOtpId && userId === User_Id) {
            setIsWashingtonOTPModalOpen(true);
        }
    }, [WashingtonOtpId]);

    const handleSubmitWashingtonOtp = async () => {
        const Otpdata = { Id: WashingtonOtpId, washington_code: washingtonOtp, LoginUser: LoginUser };

        try {
            setIsWashingtonOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/Update_Washington_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsWashingtonOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, keep modal open; WebSocket will change otp_status (V -> C/R/E)
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsWashingtonOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Washington OTP Process end

    //Wisconsin OTP Process start
    const WisconsinOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Wisconsin_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchWisconsinOTP = async () => {
            const response = await WisconsinOtp();
            if (response.length > 0 && response[0].id) {
                setWisconsinOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchWisconsinOTP();
    }, []);

    useEffect(() => {
        if (WisconsinOtpId && userId === User_Id) {
            setIsWisconsinOTPModalOpen(true);
        }
    }, [WisconsinOtpId]);

    const handleSubmitWisconsinOtp = async () => {
        const Otpdata = { Id: WisconsinOtpId, wisconsin_code: wisconsinOtp, LoginUser: LoginUser };

        try {
            setIsWisconsinOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/Update_Wisconsin_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsWisconsinOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, keep modal open; WebSocket will change otp_status (V -> C/R/E)
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsWisconsinOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Wisconsin OTP Process end

    //Illinois OTP Process start
    const IllinoisOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Illinois_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchIllinoisOTP = async () => {
            const response = await IllinoisOtp();
            if (response.length > 0 && response[0].id) {
                setIllinoisOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchIllinoisOTP();
    }, []);

    useEffect(() => {
        if (IllinoisOtpId && userId === User_Id) {
            setIsIllinoisOTPModalOpen(true);
        }
    }, [IllinoisOtpId]);

    const handleSubmitIllinoisOtp = async () => {
        const Otpdata = { Id: IllinoisOtpId, illinois_code: illinoisOtp, LoginUser: LoginUser };

        try {
            setIsIllinoisOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/Update_Illinois_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsIllinoisOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, keep modal open; WebSocket will change otp_status (V -> C/R/E)
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsIllinoisOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Illinois OTP Process end

    //Rhode Island OTP Process start
    const RhodeIslandOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_RhodeIsland_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchRhodeIslandOTP = async () => {
            const response = await RhodeIslandOtp();
            if (response.length > 0 && response[0].id) {
                setRhodeIslandOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchRhodeIslandOTP();
    }, []);

    useEffect(() => {
        if (RhodeIslandOtpId && userId === User_Id) {
            setIsRhodeIslandOTPModalOpen(true);
        }
    }, [RhodeIslandOtpId]);

    const handleSubmitRhodeIslandOtp = async () => {
        const Otpdata = { Id: RhodeIslandOtpId, rhodeIsland_code: rhodeIslandOtp, LoginUser: LoginUser };

        try {
            setIsRhodeIslandOtpVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/Update_RhodeIsland_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            if (responseData !== "OTP Updated Successfully") {
                setIsRhodeIslandOtpVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, keep modal open; WebSocket will change otp_status (V -> C/R/E)
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsRhodeIslandOtpVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Rhode Island OTP Process end

    //Oklahoma OTP Process start
    const OklahomaOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Oklahoma_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchOklahomaOTP = async () => {
            const response = await OklahomaOtp();
            if (response.length > 0 && response[0].id) {
                setOklahomaOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchOklahomaOTP();
    }, []);

    useEffect(() => {
        if (OklahomaOtpId && userId === User_Id) {
            setIsOklahomaOTPModalOpen(true);
        }
    }, [OklahomaOtpId]);

    const handleSubmitOklahomaOtp = async () => {
        const Otpdata = { Id: OklahomaOtpId, oklahoma_code: oklahomaOtp, LoginUser: LoginUser };

        try {
            setIsOklahomaVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch("api/mfa_otp/Update_Oklahoma_OTP", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsOklahomaVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsOklahomaVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Oklahoma OTP Process end

    //Amertias OTP Process start
    const AmertiasOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_Amertias_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchAmertiasOTP = async () => {
            const response = await AmertiasOtp();
            if (response.length > 0 && response[0].id) {
                setAmertiasOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchAmertiasOTP();
    }, []);

    useEffect(() => {
        if (AmertiasOtpId && userId === User_Id) {
            setIsAmertiasOTPModalOpen(true);
        }
    }, [AmertiasOtpId]);

    const handleSubmitAmertiasOtp = async () => {
        const Otpdata = { Id: AmertiasOtpId, amertias_code: amertiasOtp, LoginUser: LoginUser };

        try {
            setIsAmertiasVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch('api/mfa_otp/Update_Amertias_OTP', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsAmertiasVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsAmertiasVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Amertias OTP Process end

    //Missouri OTP Process start
    const MissouriOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_missouri_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchMissouriOTP = async () => {
            const response = await MissouriOtp();
            if (response.length > 0 && response[0].id) {
                setMissouriOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchMissouriOTP();
    }, []);

    useEffect(() => {
        if (MissouriOtpId && userId === User_Id) {
            setIsMissouriOTPModalOpen(true);
        }
    }, [MissouriOtpId]);

    const handleSubmitMissouriOtp = async () => {
        const Otpdata = { Id: MissouriOtpId, missouri_code: missouriOtp, LoginUser: LoginUser };

        try {
            setIsMissouriVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch('api/mfa_otp/Update_missouri_OTP', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsMissouriVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsMissouriVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Missouri OTP Process end


    //Toolkit OTP Process start
    const ToolkitOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_toolkit_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchToolkitOTP = async () => {
            const response = await ToolkitOtp();
            if (response.length > 0 && response[0].id) {
                setToolkitOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchToolkitOTP();
    }, []);

    useEffect(() => {
        if (toolkitOtpId && userId === User_Id) {
            setIsToolkitOTPModalOpen(true);
        }
    }, [toolkitOtpId]);

    const handleSubmitToolkitOtp = async () => {
        const Otpdata = { Id: toolkitOtpId, toolkit_code: toolkitOtp, LoginUser: LoginUser };

        try {
            setIsToolkitVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch('api/mfa_otp/Update_toolkit_OTP', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsToolkitVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsToolkitVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Toolkit OTP Process end

    //Cigna OTP Process start
    const CignaOtp = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, User_Id);
        const response = await api.fetch('api/mfa_otp/get_cigna_OTP', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchCignaOTP = async () => {
            const response = await CignaOtp();
            if (response.length > 0 && response[0].id) {
                setCignaOtpId(response[0].id);
                setUserId(response[0].Uploaded_userid);
            } else {
            }
        };
        fetchCignaOTP();
    }, []);

    useEffect(() => {
        if (cignaOtpId && userId === User_Id) {
            setIsCignaOTPModalOpen(true);
        }
    }, [cignaOtpId]);

    const handleSubmitCignaOtp = async () => {
        const Otpdata = { Id: cignaOtpId, cigna_code: cignaOtp, LoginUser: LoginUser };

        try {
            setIsCignaVerifying(true);
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Otpdata);
            const response = await api.fetch('api/mfa_otp/Update_cigna_OTP', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            const responseData = await response.json();

            // Just basic error handling – don't close modal here.
            if (responseData !== "OTP Updated Successfully") {
                setIsCignaVerifying(false);
                Swal.fire({
                    icon: "error",
                    title: "Insurance Verification Platform",
                    text: "Failed to send OTP for verification. Please try again.",
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: "btn btn-danger",
                    },
                    allowOutsideClick: false,
                });
            }

            // If successful, we STILL keep modal open and wait for WebSocket
            // to change otp_status from V -> C/R/E
        } catch (error) {
            console.error("Error submitting OTP: ", error);
            setIsCignaVerifying(false);
            Swal.fire({
                icon: "error",
                title: "Insurance Verification Platform",
                text: "An error occurred while submitting OTP.",
                showConfirmButton: true,
                customClass: {
                    confirmButton: "btn btn-danger",
                },
                allowOutsideClick: false,
            });
        }
    };
    //Cigna OTP Process end

    const get_companyname = async () => {
        const key = await keyPromise
        const response = await api.fetch('api/BulkUpload/companyname', {
            method: 'POST'
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const fetchRoleId = async () => {
            const response = await get_companyname();
            const res = response.map((data: any) => ({
                label: data.CompanyName,
                value: data.id,
            }));
            setClientOptions(res);
        };
        // Fetch the initial data
        fetchRoleId();

        // WebSocket setup
        const socket = new WebSocket(`${websocket_webservice}ws/ClientDropdown/`);
        socket.onmessage = () => {
            // Optionally, re-fetch the data to update the state when a WebSocket message is received
            fetchRoleId();
        };
        // Clean up the WebSocket connection when the component unmounts
        return () => {
            if (socket.readyState === 1) { // <-- This is important
                socket.close();
            }
        }
    }, []);

    const get_companynamevendorbased = async () => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, { id: localStorage.VendorId });
        const response = await api.fetch('api/BulkUpload/vendorbasedclientOptions', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());

        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    useEffect(() => {
        const vendorbasedfetchRoleId = async () => {
            const response = await get_companynamevendorbased();
            const res = response.map((data: any) => ({
                label: data.CompanyName,
                value: data.id,
            }));
            setvendorbasedclientOptions(res);
        };
        // Fetch the initial data
        vendorbasedfetchRoleId();
        // WebSocket setup
        const socket = new WebSocket(`${websocket_webservice}ws/ClientDropdown/`);
        // socket.onopen = () => console.log('WebSocket connected');
        // socket.onclose = () => console.log('WebSocket closed');
        // socket.onerror = (error) => console.error('WebSocket error:', error);
        socket.onmessage = () => {
            // const change = JSON.parse(event.data);
            // console.log('WebSocket message received:', change);

            // Optionally, re-fetch the data to update the state when a WebSocket message is received
            vendorbasedfetchRoleId();
        };

        // Clean up the WebSocket connection when the component unmounts
        return () => {
            if (socket.readyState === 1) { // <-- This is important
                socket.close();
            }
        }
    }, []);

    useEffect(() => {
        if (!selectedClientId) return;
        const get_Insurance = async () => {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, selectedClientId);
            const response = await api.fetch('api/Insurancename', {
                method: 'POST',
                body: JSON.stringify({ EncParams }),
            });

            const data = await response.json();
            const TempData = AesDecrypt(key, data);
            return TempData;
        };

        const fetchinsurance = async () => {
            const response = await get_Insurance();
            const res = response.map((data: any) => ({
                label: data.name,
                value: data.name,
                bot_status: data.bot_status || '',
                IsAPIEnabled: data.IsAPIEnabled || false,
            }));
            setInsurance(res);
        };

        fetchinsurance();

        const socket = new WebSocket(`${websocket_webservice}ws/InsuranceDropdown/`);
        socket.onmessage = () => {
            fetchinsurance();
        };

        // Clean up the WebSocket connection when the component unmounts
        return () => {
            if (socket.readyState === 1) { // <-- This is important
                socket.close();
            }
        }
    }, [selectedClientId]);

    const handleScreenshot_statusChange = (e: any) => {
        setScreenshot_status(e.target.value);
    };

    const handleSelectClientChange = (selectedOption: SingleValue<IClientOption>) => {
        if (selectedOption) {
            setSelectedClient(selectedOption.label);
            setSelectedClientId(selectedOption.value);

            // Clear address and template when client is changed
            setSelectedAddress(''); // Reset address
            setSelectedAddressId('');
            setAddress([]); // Assuming setAddress is setting address options
            setselectedtemplate(''); setSelectedTemplateCategory(''); // Reset template
            setselectedtemplateId('');
        } else {
            // Reset all fields if no client is selected
            setSelectedClient('');
            setSelectedClientId('');
            setSelectedAddress('');
            setSelectedAddressId('');
            setselectedtemplate(''); setSelectedTemplateCategory('');
            setselectedtemplateId('');
            setAddress([]); // Reset the address options
        }
    };

    const handleSelecttemplateChange = (selectedOption: SingleValue<IClientOption>) => {
        if (selectedOption) {
            setselectedtemplate(selectedOption.label);
            setselectedtemplateId(selectedOption.value);
        } else {

            setselectedtemplate('');
            setselectedtemplateId('');
        }
    };

    const handleTemplateCategoryChange = (selectedOption: SingleValue<IClientOption>) => {
        setSelectedTemplateCategory(selectedOption ? selectedOption.value : '');
        // Templates are filtered by type, so clear the previously chosen template
        setselectedtemplate('');
        setselectedtemplateId('');
        settemplateOptions([]);
    };

    const handleinsuranceChange = (selectedOption: SingleValue<Iinsuranceoption>) => {
        if (selectedOption) {
            setSelectedInsurance(selectedOption.label);
            setSelectedInsuranceId(selectedOption.value);
            const botStatus = (insuranceoption as any[]).find((o) => o.value === selectedOption.value)?.bot_status || '';
            setSelectedBotstatus(botStatus);
        } else {
            setSelectedInsurance('');
            setSelectedInsuranceId('');
            setSelectedBotstatus('');
        }
    };

    // Dynamic template fields are loaded only once Client, Template and Insurance
    // are all selected. They are cleared whenever any of the three changes.
    useEffect(() => {
        let cancelled = false;

        const clearDynamic = () => {
            setMatchedRpaTask(null);
            setDynamicHeaders([]);
            setDynamicValues({});
            setDynamicErrors({});
        };

        const loadDynamicFields = async () => {
            if (!selectedClientId || !selectedtemplateId || !SelectedInsuranceId || SelectedBotstatus !== 'B') {
                clearDynamic();
                return;
            }
            try {
                const result = await fetchRpaTasks({ perPage: 500, page: 1, sort: 'desc', sortColumn: 'createdAt', q: '' });
                if (cancelled) return;
                const rpaTasks: BotManagerTask[] = result.data || [];
                const matched = rpaTasks.find(
                    (t) => t.project_title?.trim().toLowerCase() === SelectedInsurancename?.trim().toLowerCase()
                ) || rpaTasks.find((t) => t.RPA_status === 'C');
                if (matched) {
                    const headers = _extractDynamicHeaders(matched.steps, matched.multiple);
                    setMatchedRpaTask(matched);
                    setDynamicHeaders(headers);
                    setDynamicValues(Object.fromEntries(headers.map((h) => [h.key, ''])));
                    setDynamicErrors({});
                } else {
                    clearDynamic();
                }
            } catch {
                if (!cancelled) clearDynamic();
            }
        };

        loadDynamicFields();
        return () => { cancelled = true; };
    }, [selectedClientId, selectedtemplateId, SelectedInsuranceId, SelectedInsurancename, SelectedBotstatus]);

    const handleAddressChange = (selectedOption: SingleValue<Addresseoption>) => {
        if (selectedOption) {
            setSelectedAddress(selectedOption.label);
            setSelectedAddressId(selectedOption.value);
        } else {
            setSelectedAddress('');
            setSelectedAddressId('');
        }
    };

    const template = async () => {
        if (!selectedClientId || !selectedTemplateCategory) {
            settemplateOptions([]);
            return;
        }
        try {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, { clientId: selectedClientId, templateCategory: selectedTemplateCategory });
            const response = await api.fetch('api/BulkUpload/template', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            const result = await response.json();
            const decryptedResult = await AesDecrypt(key, result);
            const res = decryptedResult.map((data: any) => ({
                label: data.file_name,
                value: data.id,
            }));
            settemplateOptions(res);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        template();
    }, [selectedClientId, selectedTemplateCategory]);

    useEffect(() => {
        const get_Address = async () => {
            if (!selectedClientId) {
                return;
            }
            const key = await keyPromise
            try {
                const EncParams = await AesEncrypt(key, selectedClientId);
                const fetchResponse = await api.fetch('api/address', {
                    method: 'POST',
                    body: JSON.stringify({ EncParams })
                });

                const data = await fetchResponse.json();
                const data1 = await AesDecrypt(key, data);

                const res = data1.flatMap((item: any) =>
                    item.Address.map((address: string) => ({
                        label: address,
                        value: address,
                    }))
                );

                setAddress(res);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        // Fetch the initial address data
        get_Address();

        // WebSocket setup
        const socket = new WebSocket(`${websocket_webservice}ws/ClientDropdown/`);

        // socket.onopen = () => console.log('WebSocket connected');
        // socket.onclose = () => console.log('WebSocket closed');
        // socket.onerror = (error) => console.error('WebSocket error:', error);

        socket.onmessage = () => {
            // const change = JSON.parse(event.data);
            // console.log('WebSocket message received:', change);

            // Optionally, re-fetch the data to update the state when a WebSocket message is received
            get_Address();
        };

        // Clean up the WebSocket connection when the component unmounts
        return () => {
            if (socket.readyState === 1) { // <-- This is important
                socket.close();
            }
        }
    }, [selectedClientId]);

    const deletePatient = async (ids: any[]) => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, ids);
        await api.fetch('api/bulkupload/deletePatientdetails', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        });
    };

    const DeleteAuditLogdata = async (data: any[]) => {
        data.forEach(item => {
            item.Userid = localStorage.id;
            item.ParentUserId = localStorage.ParentUserId;
        });
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, data);
        const response = await api.fetch('api/bulkupload/deleteauditlog', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then(response => response.json());
        return response;
    };

    const DeleteRow = async (rows: any[]) => {
        const ids = rows.map(row => row.id);
        const auditData = rows.map(row => ({
            Username,
            RefId: row.RefId,
            Name: row.Patientname
        }));

        const deleteRow = async () => {
            Swal.fire({
                title: "Insurance Verification Platform",
                text: "Are you sure do you want to delete?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes",
                customClass: {
                    confirmButton: "btn btn-primary",
                    cancelButton: "btn btn-danger ms-1",
                },
                buttonsStyling: false,
                allowOutsideClick: false
            })
                .then(async function (result) {
                    if (result.isConfirmed) {
                        await deletePatient(ids);
                        // ✅ Remove deleted rows from localData
                        setLocalData(prev => prev.filter(item => !ids.includes(item.id)));
                        // handleReloadTable();
                        const res = await DeleteAuditLogdata(auditData);
                        Swal.fire({
                            icon: "success",
                            title: "Insurance Verification Platform",
                            text: "Selected patient details have been deleted.",
                            customClass: {
                                confirmButton: "btn btn-success",
                            },
                            allowOutsideClick: false
                        });
                        fetchDataWithParams({});
                    }
                });
        };
        deleteRow();
    };

    const handleOpenWordDocument = async (id: any, row: any) => {
        setIsDownloading(id)
        const filename = row + '.docx';
        const key = await keyPromise
        try {
            const EncParams = await AesEncrypt(key, id);

            const response = await api.fetch('api/AllMappedPatientsData', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setIsDownloading(null)
        } catch (error) {
            setIsDownloading(null)
            console.error('Error downloading file: ', error);
        }
    };

    const handleErrorClick = async (row: any) => {
        const patientId = row.id
        setLoading(true);
        try {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, patientId);
            const response = await api.fetch("api/Email-fetchErrorDatas", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            }).then((res) => res.json());
            setLoading(false);
            setIsModalOpen(true);
            setPatientData(response[0]);
        } catch (error) {
            console.error("Error fetching patient data:", error);
        } finally {
            setLoading(false); // Hide loading after fetching data
        }
    };

    interface BootstrapCheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
        id: string;
    }

    // Handle individual row checkbox change
    const BootstrapCheckbox = forwardRef<HTMLInputElement, BootstrapCheckboxProps>(
        ({ id, ...props }, ref) => (
            <div style={{ position: 'relative', left: '10px' }}>
                <input type='checkbox' id={id} ref={ref} {...props} />
            </div>
        )
    );

    const handleChangecheck = useCallback(
        ({ selectedRows }: any) => {
            const selectedEventIds = selectedRows.map((row: any) => row.id);
            setEventId(selectedRows);
            setEventId1(selectedEventIds.length);
            if (!action.fromUser) return;
            selectedRowsPerPage[currentPage] = selectedRows;
        }, [currentPage, selectedRowsPerPage, action.fromUser]
    );

    const handleEdit = async (row: any) => {
        if (!row || typeof row !== "object") return;
        setIsEditOpen(true)
        setIsEditData(row)
        setEditId(row.id)
    };

    useEffect(() => {
        if (editOpen && editData) {
            setFormValues({
                firstName: editData.First_Name || "",
                middleName: editData.MiddleName || "",
                lastName: editData.Last_Name || "",
                memberId: editData.Memberid || "",
                patientType: editData.Type || "",
                dob: editData.PatientDOB || "",
                providerNpi: editData.Provider_NPI || "",
                insurance: editData.Payer_name || "",
                feeSchedule: "", // Set default value if needed
            });
        }
    }, [editOpen, editData]);

    const handleSubmitEditdata = async () => {
        setLoading(true);
        const data = JSON.stringify({ editId, formValues }); // Convert to string before encryption
        const auditLogData = [
            {
                field: 'Patient Name',
                before: `${editData?.First_Name} ${editData?.Last_Name}` || '',
                after: `${formValues.firstName} ${formValues.lastName}`,
            },
            {
                field: 'Date of Birth',
                before: editData?.PatientDOB || '',
                after: formValues.dob,
            },
            {
                field: 'Member ID',
                before: editData?.Memberid || '',
                after: formValues.memberId,
            },
            {
                field: 'Provider NPI',
                before: editData?.Provider_NPI || '',
                after: formValues.providerNpi,
            },
        ];
        const Username = localStorage.getItem('UserName') || '';
        const auditData = { auditLogData, Username, Refid: editData?.RefId, };
        try {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, data);
            const response = await api.fetch("api/edit_patient_data", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            })
            const responseData = await response.json();
            if (responseData === "Patient Updated Successfully") {
                // await axios.post(webservice + 'api/BulkUpload/EditauditLogdata', auditData, {
                //     headers: { 'Content-type': 'application/json; charset=UTF-8' }
                // });
                await api.fetch("api/BulkUpload/EditauditLogdata", {
                    method: "POST",
                    body: JSON.stringify({ auditData })
                })

                Swal.fire({
                    icon: "success",
                    title: "Insurance Verification Platform",
                    text: "Patient Updated Successfully",
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                    allowOutsideClick: false
                });
                setIsEditOpen(false);
                setLoading(false);
                handleReloadTable();
            }
        } catch (error) {
            console.error("Error fetching patient data:", error);
        }
    };

    const handlePortalsPDFDocument = async (id: any, row: any) => {
        const patientId = row.id;
        setLoading(true);

        try {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, patientId);
            const response = await api.fetch("api/portals-pdfs", {
                method: "POST",
                body: JSON.stringify({ EncParams })
            });

            if (!response.ok) {
                throw new Error("Failed to download file");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Extract filename from Content-Disposition header if available
            const contentDisposition = response.headers.get("Content-Disposition");
            let fileName = "portals-pdf"; // Default name

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match) {
                    fileName = match[1];
                }
            }

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading patient file:", error);
        } finally {
            setLoading(false);
        }
    };


    const ViewAuditLog = async (row: any) => {
        const Username = localStorage.UserName;
        const Userid = localStorage.id;
        const RefId = row.RefId;
        const Name = row.Patientname;
        const key = await keyPromise
        const Data = { Username, RefId, Name, Userid };
        const EncParams = await AesEncrypt(key, Data);
        const response = await api.fetch('api/BulkUpload/ViewAuditLog', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());
        return response;
    };

    const DownloadAuditLog = async (row: any) => {
        const Username = localStorage.UserName;
        const Userid = localStorage.id;
        const RefId = row.RefId;
        const Name = row.Patientname + " - " + "OutPut Downloaded";
        const key = await keyPromise
        const Data = { Username, RefId, Name, Userid };
        const EncParams = await AesEncrypt(key, Data);
        const response = await api.fetch('api/BulkUpload/DownloadAuditLog', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());
        return response;
    };
    const Role = localStorage.Role
    const [regenrateLoading, setregenrateLoading] = useState<boolean>(false);

    const handleRegenerateDocument = async (row: any) => {
        const id = row.id
        const key = await keyPromise
        // setregenrateLoading(true);
        try {
            const EncParams = await AesEncrypt(key, id);

            const fetchResponse = await api.fetch('api/Patient/regenratepatients', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });

            const auditLogData = row.First_Name + " " + row.Last_Name + " " + "Patient have been Regenrated."


            const Username = localStorage.UserName;
            const auditData = { auditLogData, Username, Refid: id }
            // const auditLogResponse = await axios.post(webservice + 'api/regenrate/regenrateauditLogdata', auditData, {
            //     headers: { 'Content-type': 'application/json; charset=UTF-8' }
            // });

            const auditLogResponse = await api.fetch('api/regenrate/regenrateauditLogdata', {
                method: 'POST',
                body: JSON.stringify({ auditData })
            });

            const data = await fetchResponse.json();
            const data1 = await AesDecrypt(key, data);

            if (data1 === "S") {
                Swal.fire({
                    icon: "success",
                    title: "Insurance Verification Platform",
                    text: "Patients have been Regenrated.",
                    customClass: {
                        confirmButton: "btn btn-success",
                    },
                    allowOutsideClick: false
                });
                handleReloadTable();
                setregenrateLoading(false);
                // navigate(`/patients`);
            }
        } catch (error) {
            console.error('Error downloading file: ', error);
        }
    };

    const Columns = [
        { id: 'UploadedBy', name: 'Uploaded by', selector: (row: { UserName: string }) => row.UserName ? row.UserName : 'NA', sortable: true, width: "160px" },
        { id: 'PatientName', name: 'Patient Name', selector: (row: { Patientname: string }) => row.Patientname, sortable: true, width: "160px" },
        { id: 'ClientName', name: 'Client Name', selector: (row: { client_name: string }) => row.client_name, sortable: true, width: "160px" },
        { id: 'TemplateName', name: 'Template Name', selector: (row: { template: string }) => row.template, sortable: true, width: "200px" },
        { id: 'PatientDOB', name: 'Patient DOB', selector: (row: { PatientDOB: string }) => row.PatientDOB, sortable: true, width: "160px" },
        { id: 'MemberId', name: 'Member ID', selector: (row: { Memberid: string }) => row.Memberid ? row.Memberid : 'NA', sortable: true, width: "160px" },
        // { name: 'Payer ID', selector: (row: { Payor_ID: string }) => row.Payor_ID, sortable: true, width: "160px" },
        { 
            id: 'InsuranceName',
            name: 'Insurance Name', 
            selector: (row: { Payer_name: string }) => row.Payer_name, 
            width: "250px", 
            sortable: true,
            cell: (row: any) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {row.LogoUrl && (
                        <img 
                            src={row.LogoUrl} 
                            alt="Logo" 
                            style={{ width: '60px', height: '60px', objectFit: 'contain', marginRight: '10px' }} 
                        />
                    )}
                    <span>{row.Payer_name || 'NA'}</span>
                </div>
            )
        },
        { id: 'Type', name: 'Type', selector: (row: { Type: string }) => row.Type, sortable: true, width: "160px" },
        { id: 'ProviderNPI', name: 'Provider NPI', selector: (row: { Provider_NPI: string }) => row.Provider_NPI ? row.Provider_NPI : 'NA', sortable: true, width: "160px" },
        { id: 'RegenerateStatus', name: 'Regenrate Status', selector: (row: { Regenratestatus: string }) => row.Regenratestatus ? row.Regenratestatus : 'NA', sortable: true, width: "160px" },
        {
            id: 'TemplateGenerationTime',
            name: (
                <>
                    Template <br /> Generation Time
                </>
            ),
            selector: (row: { template_mapping_time: string }) =>
                row.template_mapping_time ? `${row.template_mapping_time} sec` : "NA",
            sortable: true
        },
        {
            id: 'ExecutionTime',
            name: (
                <>
                    Execution Time
                </>
            ),
            selector: (row: { execution_time: string }) => row.execution_time ? `${row.execution_time} min` : "NA",
            sortable: true
        },
        { 
            id: 'BotStatus',
            name: 'BOT Status', 
            selector: (row: { Data_Fetched?: string; bot_status?: string; RPA_Status?: string }) => {
                const status = (row.bot_status !== undefined && row.bot_status !== null)
                    ? (row.RPA_Status || row.Data_Fetched)
                    : row.Data_Fetched;

                if (status === 'C' || status === 'c') return 'Received';
                if (status === 'E' || status === 'e') return 'Error';
                if (status === 'I' || status === 'i') return 'In Progress';
                if (status === 'Y' || status === 'y' || status === 'B' || status === 'b') return 'Yet To Start';
                return status || 'NA';
            }, 
            sortable: true, 
            width: "160px" 
        },
        {
            id: 'CreatedDate',
            name: 'Created Date and Time',
            selector: (row: { uploaded_date: string }) => {
                return row.uploaded_date
                    ? moment.utc(row.uploaded_date)
                        .tz(moment.tz.guess())
                        .format('MM/DD/YYYY, HH:mm:ss')
                    : 'NA';
            },
            sortable: true,
            width: "180px"
        },
        {
            id: 'Actions',
            name: "Actions",
            width: "350px",
            sortable: false,
            cell: (row: DeleteRowData) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                    {/* Fixed slot for View */}
                    <div style={{ width: "30px", textAlign: "center" }}>
                        {row.template_creation && row.Data_Fetched !== "E" && (
                            <FileText
                                size={17}
                                color={row.template_creation === "C" ? "rgba(34, 223, 16, 1)" : "#800080"}
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    navigate(`/patient-eligibility/${row.id}?view=ManualPatientView`);
                                    ViewAuditLog(row);
                                }}
                            />
                        )}
                    </div>

                    {/* Fixed slot for Download */}
                    <div style={{ width: "30px", textAlign: "center" }}>
                        {row.template_creation === "C" && row.Data_Fetched !== "E" && (
                            isDownloading === row.id ? (
                                <div className="spinner"></div>
                            ) : (
                                <Download
                                    size={20}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        handleOpenWordDocument(row.id, row.Patientname);
                                        DownloadAuditLog(row);
                                    }}
                                />
                            )
                        )}
                    </div>

                    {/* Fixed slot for Error */}
                    <div style={{ width: "20px", textAlign: "center" }}>
                        {row.Data_Fetched === "E" && (
                            <FileText
                                size={17}
                                color="red"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleErrorClick(row)}
                            />
                        )}
                    </div>

                    {/* Fixed slot for Edit */}
                    <div style={{ width: "20px", textAlign: "center" }}>
                        {(row.Data_Fetched?.trim() === "E" &&
                            (row.error_message?.includes("Error in patient details") ||
                                row.error_message === "Please enter a valid member ID")) && (
                                <Edit
                                    size={20}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleEdit(row)}
                                />
                            )}
                    </div>

                    {/* Fixed slot for PDF */}
                    <div style={{ width: "20px", textAlign: "center" }}>
                        {row.pdf_status === "yes" && (
                            <img
                                src={portaldownload}
                                alt="PDF"
                                width={20}
                                height={20}
                                style={{ cursor: "pointer" }}
                                onClick={() => handlePortalsPDFDocument(row.id, row)}
                            />
                        )}
                    </div>

                    {/* Fixed slot for Restart */}
                    <div style={{ width: "20px", textAlign: "center" }}>
                        {row.Data_Fetched?.trim() === "E" && menuActions.includes("restart") && (
                            <RotateCcw
                                size={20}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleRestart(row)}

                            />
                        )}
                    </div>

                    {/* Fixed slot for Regenerate */}
                    <div style={{ width: "30px", textAlign: "center" }}>
                        {/* {row.DataMapping === "C" && row.template_creation === "C" && row.Data_Fetched !== "E" && ( */}
                        <img
                            src={regenrate}
                            alt="Regenerate"
                            width={25}
                            height={25}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleRegenerateDocument(row)}
                            title={regenrateLoading ? "Regenerating..." : "Regenerate Document"}
                        />
                        {/* )} */}
                    </div>

                    {/* Fixed slot for Audit Log */}
                    <div style={{ width: "30px", textAlign: "center" }}>
                        {localStorage.UserType === "MOS" && row.Regenratestatus !== "Yes" && (
                            <Eye
                                size={17}
                                color="#09d5ff"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    setScrollInnerModal(true);
                                    setRefid(row.RefId);
                                }}
                            />
                        )}
                    </div>
                </div>
            ),
        }



    ]

    const { savedColumns, saveColumns } = useColumnPreferences('ManualEntriesListing');
    const columnOptions: ColumnOption[] = [
        { id: 'UploadedBy', label: 'Uploaded by' },
        { id: 'PatientName', label: 'Patient Name', locked: true },
        { id: 'ClientName', label: 'Client Name' },
        { id: 'TemplateName', label: 'Template Name' },
        { id: 'PatientDOB', label: 'Patient DOB' },
        { id: 'MemberId', label: 'Member ID' },
        { id: 'InsuranceName', label: 'Insurance Name' },
        { id: 'Type', label: 'Type' },
        { id: 'ProviderNPI', label: 'Provider NPI' },
        { id: 'RegenerateStatus', label: 'Regenrate Status' },
        { id: 'TemplateGenerationTime', label: 'Template Generation Time' },
        { id: 'ExecutionTime', label: 'Execution Time' },
        { id: 'BotStatus', label: 'BOT Status' },
        { id: 'CreatedDate', label: 'Created Date and Time' },
        { id: 'Actions', label: 'Actions', locked: true },
    ];
    const visibleColumns = Columns.filter((c) => isColVisible(columnOptions, savedColumns, c.id));

    const customStyles = {
        rows: {
            style: {
                '&:hover': {
                    backgroundColor: '#f0f0f0',

                },
            },
        },
        headCells: {
            style: {
                backgroundColor: '#355876',  // Change background color of headers
                color: 'white',  // Change text color of headers
                fontSize: '16px',  // Adjust font size of headers
            },
        },
    };

    const onSubmit = async (data: IDefaultValues) => {
        if (!selectedClient) {

            Swal.fire({
                icon: 'warning',
                title: 'Insurance Verification Platform',
                text: 'Please Select Client!',
            });
            return;

        }
        if (!selectedtemplate) {

            Swal.fire({
                icon: 'warning',
                title: 'Insurance Verification Platform',
                text: 'Please Select Template!',
            });
            return;

        }
        // if (!SelectedAddressname) {

        //     Swal.fire({
        //         icon: 'warning',
        //         title: 'Insurance Verification Platform',
        //         text: 'Please Select Address!',
        //     });
        //     return;

        // }

        // ── All dynamic template fields are mandatory ──
        if (isDynamicRpaMode) {
            const missing: Record<string, string> = {};
            dynamicHeaders.forEach((header) => {
                if (!(dynamicValues[header.key] || '').trim()) {
                    missing[header.key] = `${_dynamicFieldLabel(header.key)} is required`;
                }
            });
            setDynamicErrors(missing);
            if (Object.keys(missing).length > 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Insurance Verification Platform',
                    text: 'Please fill all the mandatory fields!',
                });
                return;
            }
        }

        setLoading(true);
        const vendorid = (localStorage.UserType === 'Vendor') ? localStorage.VendorId : "";
        const userid = localStorage.id
        const Username = localStorage.UserName;
        const Refid = uniqueUuid();

        // ── RPA dynamic mode: send dynamic_data plus any default field still shown ──
        if (isDynamicRpaMode) {
            // Only default fields that are not provided by the template are still on screen.
            const visibleDefaults: Record<string, string> = {};
            const addDefault = (fieldName: string, payloadKey: string, value?: string) => {
                if (!hiddenDefaultFields.has(fieldName)) visibleDefaults[payloadKey] = value || '';
            };
            addDefault('firstName', 'FirstName', data.firstName);
            addDefault('lastName', 'lastName', data.lastName);
            addDefault('DoB', 'DoB', data.DoB ? moment(new Date(data.DoB)).format('MM/DD/YYYY') : '');
            addDefault('Memberid', 'Memberid', data.Memberid);
            addDefault('Provider_or_Company_name', 'Provider_or_Company_name', data.Provider_or_Company_name);
            addDefault('ProviderNPI', 'ProviderNPI', data.ProviderNPI);
            addDefault('providerTaxid', 'providerTaxid', data.providerTaxid);
            addDefault('city', 'city', data.city);
            addDefault('state', 'state', data.state);
            addDefault('postalCode', 'postalCode', data.postalCode);
            addDefault('Subscriber', 'Subscriber', data.Subscriber);
            addDefault('Subscriber_First_Name', 'subscriberFirstName', data.Subscriber_First_Name);
            addDefault('Subscriber_Last_Name', 'subscriberLastName', data.Subscriber_Last_Name);
            addDefault('Subscriber_DOB', 'subscriberDob', data.Subscriber_DOB);
            addDefault('Subscriber_Member_ID', 'subscriberMemberId', data.Subscriber_Member_ID);
            addDefault('InsuranceNetwork', 'InsuranceNetwork', data.InsuranceNetwork);
            addDefault('FeeSchedule', 'Fee_Schedule', data.FeeSchedule);

            // The bot previously received these default values inside dynamic_data.
            // Keep that contract for the ones the template does not override.
            const legacyDynamicKeys: Array<[string, string, string | undefined]> = [
                ['firstName', 'FirstName', data.firstName],
                ['lastName', 'LastName', data.lastName],
                ['city', 'city', data.city],
                ['state', 'state', data.state],
                ['postalCode', 'postal_code', data.postalCode],
                ['Subscriber', 'patient_type', data.Subscriber],
                ['FeeSchedule', 'fee_schedule', data.FeeSchedule],
                ['InsuranceNetwork', 'insurance_network', data.InsuranceNetwork],
            ];
            const dynamicData: Record<string, string> = { ...dynamicValues };
            legacyDynamicKeys.forEach(([fieldName, legacyKey, value]) => {
                if (!hiddenDefaultFields.has(fieldName)) dynamicData[legacyKey] = value || '';
            });

            const rpaPayload = [{
                ...visibleDefaults,
                ClientId: selectedClientId,
                ClientName: selectedClient,
                template: selectedtemplate,
                templateId: selectedtemplateId,
                Address: SelectedAddressname,
                Username,
                screenshot_status: Screenshot_status,
                Payer_name: SelectedInsurancename,
                Uploaded_userid: userid,
                Patients_Count: 1,
                vendor_id: vendorid,
                bot_status: SelectedBotstatus,
                rpa_bot_id: matchedRpaTask?.id || '',
                uploaded_status: 'Manual',
                dynamic_fields: dynamicHeaders.map((h) => ({ key: h.key, prompt: h.prompt })),
                dynamic_data: dynamicData,
            }];
            try {
                const key = await keyPromise;
                const EncParams = await AesEncrypt(key, rpaPayload);
                const response = await api.fetch('api/bulkupload/addBulkPatientdetails', {
                    method: 'POST',
                    body: JSON.stringify({ EncParams })
                }).then((r) => r.json());
                if (response?.message === 'Bulk upload successful') {
                    Swal.fire({ icon: 'success', title: 'Insurance Verification Platform', text: 'Patient added successfully!' });

                    // Trigger RPA automation
                    try {
                        const rpaKey = await keyPromise;
                        const rpaEncParams = await AesEncrypt(rpaKey, {
                            rpa_bot_id: matchedRpaTask?.id,
                            user_id: userid,
                        });
                        const rpaResponse = await api.fetch('api/bulkupload/triggerRpaBulkAutomation', {
                            method: 'POST',
                            body: JSON.stringify({ EncParams: rpaEncParams }),
                        }).then((r) => r.json());

                        if (rpaResponse.status === 'success') {
                            Swal.fire({
                                title: 'RPA Automation Triggered',
                                html: `<span style="color: blue;">RPA automation started for ${rpaResponse.patients_count} patients!</span>`,
                                icon: 'info',
                                timer: 3000,
                                showConfirmButton: false,
                            });
                        } else {
                            Swal.fire({
                                title: 'RPA Automation Failed',
                                html: `<span style="color: orange;">${rpaResponse.message || 'Unknown error'}</span>`,
                                icon: 'warning',
                                timer: 3000,
                                showConfirmButton: false,
                            });
                        }
                    } catch {
                        Swal.fire({
                            title: 'RPA Automation Error',
                            html: '<span style="color: red;">Error connecting to RPA automation service</span>',
                            icon: 'error',
                            timer: 3000,
                            showConfirmButton: false,
                        });
                    }

                    fetchDataWithParams({});
                    reset();
                    setDynamicValues({});
                    setDynamicHeaders([]);
                    setDynamicErrors({});
                    setMatchedRpaTask(null);
                    setSelectedBotstatus('');
                    setSelectedClient('');
                    setSelectedClientId('');
                    setselectedtemplate(''); setSelectedTemplateCategory('');
                    setselectedtemplateId('');
                    setSelectedInsurance('');
                    setSelectedInsuranceId('');
                } else {
                    Swal.fire({ icon: 'error', title: 'Error', text: response?.message || 'Failed to save patient' });
                }
            } catch (err: any) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to save patient' });
            }
            setLoading(false);
            return;
        }
        // ─────────────────────────────────────────────────────────────────────

        const dateofbirth = new Date(data.DoB);
        const day = dateofbirth.getDate().toString().padStart(2, "0");
        const month = (dateofbirth.getMonth() + 1).toString().padStart(2, "0");
        const year = dateofbirth.getFullYear();
        const FullDate = month + "/" + day + "/" + year;

        const payload = {
            ClientId: selectedClientId,
            ClientName: selectedClient,
            template: selectedtemplate,
            templateId: selectedtemplateId,
            FirstName: data.firstName,
            MiddleName: data.MiddleName,
            lastName: data.lastName,
            DoB: FullDate,
            Username: Username,
            ProviderNPI: data.ProviderNPI,
            vendor_id: vendorid,
            Uploaded_userid: userid,
            // PayorID: data.PayorID,
            Memberid: data.Memberid,
            Insurancename: SelectedInsurancename,
            // InsuranceID: SelectedInsuranceId,
            Address: SelectedAddressname,
            AddressId: SelectedAddressId,
            Subscriber: data.Subscriber,
            Fee_Schedule: data.FeeSchedule,
            Provider_or_Company_name: data.Provider_or_Company_name,
            providerTaxid: data.providerTaxid,
            city: data.city,
            state: data.state,
            postalCode: data.postalCode,
            subscriberFirstName: data.Subscriber_First_Name,
            subscriberLastName: data.Subscriber_Last_Name,
            subscriberDob: data.Subscriber_DOB,
            subscriberMemberId: data.Subscriber_Member_ID,
            // location: data.location,
            InsuranceNetwork: data.InsuranceNetwork,
            screenshot_status: Screenshot_status,
            Refid: Refid,

        }

        const auditLogData = [

            {
                field: "Uploaded Type",
                value: "Manual",
            },
            {
                field: "Patient Name",
                value: data.firstName + " " + data.lastName,
            },
            {
                field: "Client Name",
                value: selectedClient,
            },
            {
                field: "Template Name",
                value: selectedtemplate,
            },
            {
                field: "Patient DOB",
                value: FullDate,
            },
            {
                field: "Memberid",
                value: data.Memberid,
            },
            // {
            //     field: "Payer ID",
            //     value: data.PayorID,
            // },
            {
                field: "Provider NPI",
                value: data.ProviderNPI,
            },
            {
                field: "Insurance",
                value: SelectedInsurancename,
            },
            // {
            //     field: "Address",
            //     value: SelectedAddressname,
            // },
            {
                field: "Subscriber",
                value: data.Subscriber,
            },
            {
                field: "Screenshot Status",
                value: Screenshot_status
            },
            {
                field: "Provider or Company name",
                value: data.Provider_or_Company_name
            },
            {
                field: "Provider Tax Id",
                value: data.providerTaxid
            },
            {
                field: "City",
                value: data.city
            },
            {
                field: "State",
                value: data.state
            },
            {
                field: "Zip Code",
                value: data.postalCode
            },
            {
                field: "Subscriber First Name",
                value: data.Subscriber_First_Name
            },
            {
                field: "Subscriber Last Name",
                value: data.Subscriber_Last_Name
            },
            {
                field: "Subscriber DOB",
                value: data.Subscriber_DOB
            },
            {
                field: "Subscriber Member ID",
                value: data.Subscriber_Member_ID
            },
            // {
            //     field: "Location",
            //     value: data.location
            // },
            {
                field: "Insurance Network",
                value: data.InsuranceNetwork
            }


        ]

        const auditData = { auditLogData, Username, Refid }
        const key = await keyPromise

        const EncParams = await AesEncrypt(key, payload);

        //  api/bulkupload/addPatientdetails

        const response = await api.fetch('api/bulkupload/addPatientdetails', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then(response => response.json());

        if (response === "success" || response?.message === "success") {
            const noOtpInsurances = ['Blue Cross Blue Shield of Texas', 'UCCI', 'Central States', 'DNOA'];
            const isBatchApiEnabled = !!insuranceoption.find((option) => option.value === SelectedInsurancename)?.IsAPIEnabled;
            const isNoOtp = isBatchApiEnabled || noOtpInsurances.includes(SelectedInsurancename);

            if (!isNoOtp) {
                startOtpProcessing();
                localStorage.setItem('currentBatchInsuranceName', SelectedInsurancename);
                if (response?.patient_id) {
                    localStorage.setItem('currentBatchPatientIds', JSON.stringify([response.patient_id]));
                }
            }

            fetchDataWithParams({});
            // const auditLogResponse = await axios.post(webservice + 'api/BulkUpload/AddauditLogdata', auditData, {
            //     headers: { 'Content-type': 'application/json; charset=UTF-8' }
            // });
            const auditLogResponse = await api.fetch('api/BulkUpload/AddauditLogdata', {
                method: 'POST',
                body: JSON.stringify({ auditData })
            }).then(response => response.json());
            
            Swal.fire({
                html: `
                <div style="text-align: center;">
                    <span style="color: green; font-weight: bold; font-size: 1.2em;">Patient Added successfully!</span>
                    ${isNoOtp ? '' : `
                        <br/><br/><span>OTP Authentication in progress for <b>${SelectedInsurancename}</b>, please wait...</span>
                        <br/><br/>
                        <div style="background-color: #fff3cd; color: #856404; padding: 10px; border-radius: 5px; border: 1px solid #ffeeba; margin-top: 10px;">
                            <strong>⚠️ Warning:</strong> Please do not refresh the page until the authentication process is complete.
                        </div>
                    `}
                </div>
            `,
                icon: 'success',
                allowOutsideClick: isNoOtp,
                allowEscapeKey: isNoOtp,
                showConfirmButton: isNoOtp,
                // The OTP wait closes on its own after 30 minutes
                timer: isNoOtp ? undefined : OTP_POPUP_MAX_MS,
                showClass: {
                    popup: 'animate__animated animate__flipInX',
                },
                didOpen: () => {
                    if (!isNoOtp) {
                        Swal.showLoading();
                    }
                }
            }).then(onOtpPopupClosed);
            setLoading(false);
            togglepatient();
            reset();

            setSelectedClient('');
            setSelectedClientId('');
            setselectedtemplate(''); setSelectedTemplateCategory('');
            setselectedtemplateId('');
            handleReloadTable();
            setSelectedAddress('');
            setSelectedAddressId('');
            setAddress([]);
        }
    };




    const CustomLoadingComponent = () => {
        return (
            <div className="">
                {store.no_Data === 'There is no Record Found' ? (
                    <h4>
                        <br />
                        <p>There are no records to display</p>
                    </h4>
                ) : (
                    <div className="custom-loader">
                        <label className="custom-label">Please wait...</label>
                        <div className="custom-loading"></div>
                    </div>
                )}
            </div>
        );
    };


    const getAuditlogdata = async (data: any) => {
        const key = await keyPromise
        const EncParams = await AesEncrypt(key, data);
        const response = await api.fetch('api/Bulk-upload-patients/get-audit-log-data', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());
        const TempData = await AesDecrypt(key, response);

        return TempData;
    };

    useEffect(() => {
        const fetchData = async () => {
            if (refid !== null) {
                const res = await getAuditlogdata(refid);
                setFilteredAuditData(res);
            }
        };
        fetchData();
    }, [refid]);

    const formatText = (text: any): string => {
        if (!text) return "";
        let str = String(text).toLowerCase(); // make lowercase
        return str.charAt(0).toUpperCase() + str.slice(1); // capitalize first letter
    };
    return (
        <Fragment>
            <Card>
                <CardBody>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <>
                            <div className="d-flex p-1 border" style={{ backgroundColor: "white" }}>
                                <CardBody>
                                    <Row>
                                        {localStorage.UserType === 'MOS' && (
                                            <Col xl={3}>
                                                <Label className="form-label" style={{ fontSize: "110%" }}>
                                                    Select Client <span style={{ color: "red" }}> * </span>
                                                </Label>
                                                <Select
                                                    isClearable
                                                    options={clientOptions}
                                                    classNamePrefix="select"
                                                    placeholder="Client Names"

                                                    className={classnames("form-control", {
                                                        "is-invalid": !!errors.ClientName,
                                                    })}
                                                    styles={{
                                                        control: (styles) => ({
                                                            ...styles,
                                                            width: "100%",
                                                            borderColor: "black",
                                                        }),
                                                        placeholder: (styles) => ({
                                                            ...styles,
                                                            textAlign: "center",
                                                        }),
                                                    }}
                                                    value={selectedClient ? { value: selectedClientId, label: selectedClient } : ""}
                                                    onChange={(selectedOption) => {
                                                        handleSelectClientChange(selectedOption as SingleValue<IClientOption>);
                                                    }}
                                                />
                                                {errors.ClientName && (
                                                    <span style={{ color: "red", fontSize: "12px" }}>
                                                        {errors.ClientName.message} {/* Display the error message */}
                                                    </span>
                                                )}
                                            </Col>
                                        )}
                                        {localStorage.UserType === 'Vendor' && (
                                            <Col xl={3}>
                                                <Label className="form-label" style={{ fontSize: "110%" }}>
                                                    Select Client <span style={{ color: "red" }}> * </span>
                                                </Label>
                                                <Select
                                                    isClearable
                                                    options={vendorbasedclientOptions}
                                                    classNamePrefix="select"
                                                    placeholder="Type Here"
                                                    className={classnames("form-control", {
                                                        "is-invalid": !!errors.ClientName,
                                                    })}
                                                    styles={{
                                                        control: (styles) => ({
                                                            ...styles,
                                                            width: "100%",
                                                            borderColor: "black",
                                                        }),
                                                        placeholder: (styles) => ({
                                                            ...styles,
                                                            textAlign: "center",
                                                        }),
                                                    }}
                                                    value={selectedClient ? { value: selectedClientId, label: selectedClient } : ""}
                                                    onChange={(selectedOption) => {
                                                        handleSelectClientChange(selectedOption as SingleValue<IClientOption>);
                                                    }}
                                                />
                                                {errors.ClientName && (
                                                    <span style={{ color: "red", fontSize: "12px" }}>
                                                        {errors.ClientName.message} {/* Display the error message */}
                                                    </span>
                                                )}
                                            </Col>
                                        )}

                                        <Col xl={3}>
                                            <Label className="form-label" style={{ fontSize: "110%" }}>
                                                Select Template Type <span style={{ color: "red" }}> * </span>
                                            </Label>
                                            <Select
                                                isClearable
                                                options={TEMPLATE_CATEGORY_OPTIONS}
                                                classNamePrefix="select"
                                                placeholder="Select Type"
                                                className="form-control"
                                                styles={{
                                                    control: (styles) => ({
                                                        ...styles,
                                                        width: "100%",
                                                        borderColor: "black",
                                                    }),
                                                    placeholder: (styles) => ({
                                                        ...styles,
                                                        textAlign: "center",
                                                    }),
                                                }}
                                                value={selectedTemplateCategory ? { value: selectedTemplateCategory, label: selectedTemplateCategory } : null}
                                                onChange={(selectedOption) => {
                                                    handleTemplateCategoryChange(selectedOption as SingleValue<IClientOption>);
                                                }}
                                            />
                                        </Col>
                                        <Col xl={3}>
                                            <Label className="form-label" style={{ fontSize: "110%" }}>
                                                Select Template <span style={{ color: "red" }}> * </span>
                                            </Label>
                                            <Select
                                                isClearable
                                                options={templateOptions}
                                                // value={clientOptions.find((e)=>e.label===selectedClient)}
                                                classNamePrefix="select"
                                                placeholder="Type Here"
                                                //   theme={selectThemeColors}  
                                                className={classnames("form-control", {
                                                    "is-invalid": errors.ClientName && true,
                                                })}
                                                // {...field}
                                                styles={{
                                                    control: (styles) => ({
                                                        ...styles,
                                                        width: "100%",
                                                        borderColor: "black",
                                                    }),
                                                    placeholder: (styles) => ({
                                                        ...styles,
                                                        textAlign: "center",
                                                    }),
                                                }}
                                                value={selectedtemplate ? { value: selectedtemplateId, label: selectedtemplate } : ""}
                                                onChange={(selectedOption) => {
                                                    // field.onChange(selectedOption);
                                                    handleSelecttemplateChange(selectedOption as SingleValue<IClientOption>);
                                                }}

                                            />
                                            {errors.ClientName && (
                                                <span style={{ color: "red", fontSize: "12px" }}>
                                                    {errors.ClientName.message}
                                                </span>
                                            )}
                                        </Col>
                                        <Col xl={3}>
                                            <Label className="form-label">
                                                Select Insurance<span style={{ color: 'red' }}> * </span>
                                            </Label>
                                            <Controller
                                                name="Insurancename"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select<Iinsuranceoption>
                                                        isClearable
                                                        {...(field as any)}
                                                        options={insuranceoption}
                                                        classNamePrefix="select"
                                                        className={classnames("form-control", {
                                                            "is-invalid": !!errors.Insurancename,
                                                        })}
                                                        onChange={(selectedOption) => {
                                                            field.onChange(selectedOption);
                                                            handleinsuranceChange(selectedOption as SingleValue<Iinsuranceoption>);
                                                        }}
                                                    />
                                                )}
                                                rules={{ required: "Insurance is required" }}

                                            />
                                            {errors.Insurancename && (
                                                <FormFeedback className="text-danger">
                                                    {errors.Insurancename.message}
                                                </FormFeedback>
                                            )}
                                        </Col>
                                        <Row style={{ marginTop: "2%" }}>
                                            <Col xl={12}>
                                                <h4>
                                                    <b>Patient Details:</b>
                                                </h4>
                                            </Col>
                                        </Row>

                                        {/* ── RPA dynamic fields (all mandatory) ── */}
                                        {isDynamicRpaMode && (
                                            <Row style={{ marginTop: "2%" }}>
                                                {dynamicHeaders.map((header) => {
                                                    const handleDynamicChange = (value: string) => {
                                                        setDynamicValues((prev) => ({ ...prev, [header.key]: value }));
                                                        setDynamicErrors((prev) => {
                                                            if (!prev[header.key]) return prev;
                                                            const next = { ...prev };
                                                            delete next[header.key];
                                                            return next;
                                                        });
                                                    };
                                                    const hasError = !!dynamicErrors[header.key];
                                                    return (
                                                    <Col xl={3} key={header.key} className="mb-3">
                                                        <Label className="form-label">
                                                            {_dynamicFieldLabel(header.key)} <span style={{ color: "red" }}>* </span>
                                                        </Label>
                                                        {header.key === 'patient_type' ? (
                                                            <Input
                                                                type="select"
                                                                invalid={hasError}
                                                                value={dynamicValues[header.key] || ''}
                                                                onChange={(e) => handleDynamicChange(e.target.value)}
                                                            >
                                                                <option value="">Select Type</option>
                                                                <option value="self">Self</option>
                                                                <option value="dependent">Dependent</option>
                                                            </Input>
                                                        ) : header.key === 'fee_schedule' ? (
                                                            <Input
                                                                type="select"
                                                                invalid={hasError}
                                                                value={dynamicValues[header.key] || ''}
                                                                onChange={(e) => handleDynamicChange(e.target.value)}
                                                            >
                                                                <option value="">Select...</option>
                                                                <option value="PPO">PPO</option>
                                                                <option value="PREMIER">PREMIER</option>
                                                                <option value="NONPARTICIPATING">NONPARTICIPATING</option>
                                                            </Input>
                                                        ) : (
                                                            <Input
                                                                type="text"
                                                                invalid={hasError}
                                                                value={dynamicValues[header.key] || ''}
                                                                placeholder={header.prompt}
                                                                onChange={(e) => handleDynamicChange(e.target.value)}
                                                            />
                                                        )}
                                                        {hasError && (
                                                            <FormFeedback>
                                                                {dynamicErrors[header.key]}
                                                            </FormFeedback>
                                                        )}
                                                    </Col>
                                                    );
                                                })}
                                            </Row>
                                        )}

                                        {/* ── Default fields: each one is hidden when the template supplies it ── */}
                                        <>
                                        <Row style={{ marginTop: "2%" }}>
                                        {!isDefaultFieldHidden('firstName') && (
                                        <Col xl={3}>
                                                <Label className="form-label" htmlFor="firstName">
                                                    First Name <span style={{ color: "red" }}>* </span>
                                                </Label>
                                                <Controller
                                                    control={control}
                                                    name="firstName"
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            {...field}
                                                            id="firstName"
                                                            placeholder=""
                                                            invalid={!!errors.firstName}
                                                            onChange={(e) => {
                                                                let inputValue = e.target.value.replace(/[^A-Za-z]/gi, "");
                                                                if (inputValue.length > 0) {
                                                                    inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                                                                }
                                                                field.onChange(inputValue);
                                                            }}
                                                        />
                                                    )}
                                                    rules={{ required: "First Name is required" }}
                                                />
                                                {errors.firstName && (
                                                    <FormFeedback>
                                                        {errors.firstName.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('lastName') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="lastName">
                                                    Last Name <span style={{ color: "red" }}>* </span>
                                                </Label>
                                                <Controller
                                                    name="lastName"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            id="lastName"
                                                            placeholder=""
                                                            invalid={!!errors.lastName}
                                                            onChange={(e) => {
                                                                let inputValue = e.target.value.replace(/[^A-Za-z]/gi, "");
                                                                if (inputValue.length > 0) {
                                                                    inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                                                                }
                                                                field.onChange(inputValue);
                                                            }}
                                                        />
                                                    )}
                                                    rules={{ required: "Last Name is required" }}
                                                />
                                                {errors.lastName && (
                                                    <FormFeedback>
                                                        {errors.lastName.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('DoB') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="DoB">
                                                    Date of Birth <span style={{ color: "red" }}>*</span>
                                                </Label>
                                                <Controller
                                                    control={control}
                                                    name="DoB"
                                                    rules={{
                                                        required: "Date of Birth is required",
                                                        validate: (value) => {
                                                            const parsedDate = new Date(value);
                                                            if (isNaN(parsedDate.getTime())) {
                                                                return "Invalid date format";
                                                            }
                                                            if (parsedDate > new Date()) {
                                                                return "Date of Birth cannot be in the future";
                                                            }
                                                            return true;
                                                        },
                                                    }}

                                                    render={({ field }) => {
                                                        const handleDateChange = (date: Date | null) => {
                                                            if (date && date > new Date()) {
                                                                field.onChange("");
                                                            } else {
                                                                field.onChange(date ? date.toLocaleDateString("en-US") : "");
                                                            }
                                                        };

                                                        return (
                                                            <InputGroup>
                                                                <DatePicker
                                                                    selected={field.value ? new Date(field.value) : null}
                                                                    onChange={handleDateChange}
                                                                    dateFormat="MM/dd/yyyy"
                                                                    placeholderText="MM/DD/YYYY"
                                                                    className={classnames("form-control", {
                                                                        "is-invalid": !!errors.DoB,
                                                                    })}
                                                                    showPopperArrow={false}
                                                                    showMonthDropdown
                                                                    showYearDropdown
                                                                    dropdownMode="select" // makes it a dropdown instead of scroll
                                                                    scrollableYearDropdown
                                                                    yearDropdownItemNumber={100} // show 100 years (can adjust)
                                                                />

                                                                <InputGroupText
                                                                    onClick={() => {
                                                                        const datePickerInput = document.querySelector(".react-datepicker__input-container input") as HTMLInputElement;
                                                                        if (datePickerInput) {
                                                                            datePickerInput.focus();
                                                                        }
                                                                    }}
                                                                >
                                                                    <FaCalendarAlt />
                                                                </InputGroupText>

                                                            </InputGroup>
                                                        );
                                                    }}
                                                />
                                                {errors.DoB && (
                                                    <p className="error-message" style={{ color: "#db3939", width: "100%", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                                                        {errors.DoB.message}
                                                    </p>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('Memberid') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="Memberid">
                                                    Member ID <span style={{ color: "red" }}>* </span>
                                                </Label>
                                                <Controller
                                                    name="Memberid"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="text"
                                                            {...field}
                                                            id="Memberid"
                                                            placeholder=""
                                                            invalid={!!errors.Memberid}
                                                            maxLength={15}

                                                        />
                                                    )}
                                                    rules={{ required: "Member ID is required" }}
                                                />
                                                {errors.Memberid && (
                                                    <FormFeedback>
                                                        {errors.Memberid.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                        </Row>
                                        <Row style={{ marginTop: "2%" }}>
                                            {!isDefaultFieldHidden('Provider_or_Company_name') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="Provider_or_Company_name">
                                                    Provider or Company name
                                                </Label>
                                                <Controller
                                                    name="Provider_or_Company_name"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="Provider_or_Company_name"
                                                            placeholder=""
                                                            invalid={!!errors.Provider_or_Company_name}
                                                            maxLength={100}
                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.Provider_or_Company_name && (
                                                    <FormFeedback>
                                                        {errors.Provider_or_Company_name.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('ProviderNPI') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="ProviderNPI">
                                                    Provider / Group NPI
                                                </Label>
                                                <Controller
                                                    name="ProviderNPI"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="ProviderNPI"
                                                            placeholder=""
                                                            invalid={!!errors.ProviderNPI}
                                                            maxLength={15}

                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.ProviderNPI && (
                                                    <FormFeedback>
                                                        {errors.ProviderNPI.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('providerTaxid') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="providerTaxid">
                                                    Provider Tax ID
                                                </Label>
                                                <Controller
                                                    name="providerTaxid"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="providerTaxid"
                                                            placeholder=""
                                                            invalid={!!errors.providerTaxid}
                                                            maxLength={20}

                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.providerTaxid && (
                                                    <FormFeedback>
                                                        {errors.providerTaxid.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('city') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="city">
                                                    City
                                                </Label>
                                                <Controller
                                                    name="city"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="City"
                                                            placeholder=""
                                                            invalid={!!errors.city}
                                                            maxLength={100}

                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.city && (
                                                    <FormFeedback>
                                                        {errors.city.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                        </Row>
                                        <Row style={{ marginTop: "3%" }}>
                                            {!isDefaultFieldHidden('state') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="state">
                                                    State
                                                </Label>
                                                <Controller
                                                    name="state"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="state"
                                                            placeholder=""
                                                            invalid={!!errors.state}
                                                            maxLength={100}

                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.state && (
                                                    <FormFeedback>
                                                        {errors.state.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('postalCode') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="postalCode">
                                                    Zip Code
                                                </Label>
                                                <Controller
                                                    name="postalCode"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="City"
                                                            placeholder=""
                                                            invalid={!!errors.postalCode}
                                                            maxLength={100}

                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.postalCode && (
                                                    <FormFeedback>
                                                        {errors.postalCode.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            {!isDefaultFieldHidden('facility') && (
                                            <Col xl={3}>
                                                <Label className="form-label" htmlFor="facility">
                                                    Facility
                                                </Label>
                                                <Controller
                                                    name="facility"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <Input
                                                            // type="number"
                                                            {...field}
                                                            id="facility"
                                                            placeholder=""
                                                            invalid={!!errors.facility}
                                                            maxLength={100}

                                                        />
                                                    )}
                                                // rules={{ required: "Provider NPI ID is required" }}
                                                />
                                                {errors.facility && (
                                                    <FormFeedback>
                                                        {errors.facility.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}

                                            {!isDefaultFieldHidden('Subscriber') && (
                                            <Col xl={3}>
                                                <Label className="form-label">Patient Type<span style={{ color: 'red' }}> * </span></Label>
                                                <Controller
                                                    name="Subscriber"
                                                    control={control}
                                                    rules={{ required: "Patient type is required" }}
                                                    render={({ field }) => (
                                                        <Select<IOption>
                                                            {...field}
                                                            value={SubscriberOptions.find(option => option.value === field.value) || null}
                                                            options={SubscriberOptions}
                                                            getOptionLabel={(option) => option.label}
                                                            getOptionValue={(option) => option.value}
                                                            className={classnames("form-control", {
                                                                "is-invalid": !!errors.Subscriber,
                                                            })}
                                                            classNamePrefix="select"
                                                            onChange={(selectedOption: SingleValue<IOption>) => field.onChange(selectedOption?.value)}
                                                        />
                                                    )}
                                                />
                                                {errors.Subscriber && (
                                                    <FormFeedback>
                                                        {errors.Subscriber.message}
                                                    </FormFeedback>
                                                )}
                                            </Col>
                                            )}
                                            <Row style={{ marginTop: "2%" }}>
                                                {!isDefaultFieldHidden('Subscriber_First_Name') && (
                                                <Col xl={3}>
                                                    <Label className="form-label" htmlFor="Subscriber_First_Name">
                                                        Subscriber First Name
                                                    </Label>
                                                    <Controller
                                                        control={control}
                                                        name="Subscriber_First_Name"
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                {...field}
                                                                id="Subscriber_First_Name"
                                                                placeholder=""
                                                                invalid={!!errors.Subscriber_First_Name}
                                                                onChange={(e) => {
                                                                    let inputValue = e.target.value.replace(/[^A-Za-z]/gi, "");
                                                                    if (inputValue.length > 0) {
                                                                        inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                                                                    }
                                                                    field.onChange(inputValue);
                                                                }}
                                                            />
                                                        )}
                                                        rules={{ required: "First Name is required" }}
                                                    />
                                                    {errors.Subscriber_First_Name && (
                                                        <FormFeedback>
                                                            {errors.Subscriber_First_Name.message}
                                                        </FormFeedback>
                                                    )}
                                                </Col>
                                                )}
                                                {!isDefaultFieldHidden('Subscriber_Last_Name') && (
                                                <Col xl={3}>
                                                    <Label className="form-label" htmlFor="Subscriber_Last_Name">
                                                        Subscriber Last Name
                                                    </Label>
                                                    <Controller
                                                        name="Subscriber_Last_Name"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                id="Subscriber_Last_Name"
                                                                placeholder=""
                                                                invalid={!!errors.Subscriber_Last_Name}
                                                                onChange={(e) => {
                                                                    let inputValue = e.target.value.replace(/[^A-Za-z]/gi, "");
                                                                    if (inputValue.length > 0) {
                                                                        inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                                                                    }
                                                                    field.onChange(inputValue);
                                                                }}
                                                            />
                                                        )}
                                                        rules={{ required: "Last Name is required" }}
                                                    />
                                                    {errors.Subscriber_Last_Name && (
                                                        <FormFeedback>
                                                            {errors.Subscriber_Last_Name.message}
                                                        </FormFeedback>
                                                    )}
                                                </Col>
                                                )}
                                                {!isDefaultFieldHidden('Subscriber_DOB') && (
                                                <Col xl={3}>
                                                    <Label className="form-label" htmlFor="DoB">
                                                        Subscriber DOB
                                                    </Label>
                                                    <Controller
                                                        control={control}
                                                        name="Subscriber_DOB"
                                                        rules={{
                                                            required: "Date of Birth is required",
                                                            validate: (value) => {
                                                                const parsedDate = new Date(value);
                                                                if (isNaN(parsedDate.getTime())) {
                                                                    return "Invalid date format";
                                                                }
                                                                if (parsedDate > new Date()) {
                                                                    return "Date of Birth cannot be in the future";
                                                                }
                                                                return true;
                                                            },
                                                        }}

                                                        render={({ field }) => {
                                                            const handleDateChange = (date: Date | null) => {
                                                                if (date && date > new Date()) {
                                                                    field.onChange("");
                                                                } else {
                                                                    field.onChange(date ? date.toLocaleDateString("en-US") : "");
                                                                }
                                                            };

                                                            return (
                                                                <InputGroup>
                                                                    <DatePicker
                                                                        selected={field.value ? new Date(field.value) : null}
                                                                        onChange={handleDateChange}
                                                                        dateFormat="MM/dd/yyyy"
                                                                        placeholderText="MM/DD/YYYY"
                                                                        className={classnames("form-control", {
                                                                            "is-invalid": !!errors.DoB,
                                                                        })}
                                                                        showPopperArrow={false}
                                                                        showMonthDropdown
                                                                        showYearDropdown
                                                                        dropdownMode="select" // makes it a dropdown instead of scroll
                                                                        scrollableYearDropdown
                                                                        yearDropdownItemNumber={100} // show 100 years (can adjust)
                                                                    />

                                                                    <InputGroupText
                                                                        onClick={() => {
                                                                            const datePickerInput = document.querySelector(".react-datepicker__input-container input") as HTMLInputElement;
                                                                            if (datePickerInput) {
                                                                                datePickerInput.focus();
                                                                            }
                                                                        }}
                                                                    >
                                                                        <FaCalendarAlt />
                                                                    </InputGroupText>

                                                                </InputGroup>
                                                            );
                                                        }}
                                                    />
                                                    {errors.Subscriber_DOB && (
                                                        <p className="error-message" style={{ color: "#db3939", width: "100%", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                                                            {errors.Subscriber_DOB.message}
                                                        </p>
                                                    )}
                                                </Col>
                                                )}
                                                {!isDefaultFieldHidden('Subscriber_Member_ID') && (
                                                <Col xl={3}>
                                                    <Label className="form-label" htmlFor="Subscriber_Member_ID">
                                                        Subscriber Member ID
                                                    </Label>
                                                    <Controller
                                                        name="Subscriber_Member_ID"
                                                        control={control}
                                                        defaultValue=""
                                                        render={({ field }) => (
                                                            <Input
                                                                // type="text"
                                                                {...field}
                                                                id="Subscriber_Member_ID"
                                                                placeholder=""
                                                                invalid={!!errors.Subscriber_Member_ID}
                                                                maxLength={15}

                                                            />
                                                        )}
                                                        rules={{ required: "Member ID is required" }}
                                                    />
                                                    {errors.Subscriber_Member_ID && (
                                                        <FormFeedback>
                                                            {errors.Subscriber_Member_ID.message}
                                                        </FormFeedback>
                                                    )}
                                                </Col>
                                                )}

                                            </Row>
                                            <Row style={{ marginTop: "2%" }}>
                                                {!isDefaultFieldHidden('InsuranceNetwork') && (
                                                <Col xl={3}>
                                                    <Label className="form-label" htmlFor="InsuranceNetwork">
                                                        Insurance Network
                                                    </Label>
                                                    <Controller
                                                        name="InsuranceNetwork"
                                                        control={control}
                                                        defaultValue=""
                                                        render={({ field }) => (
                                                            <Input
                                                                // type="number"
                                                                {...field}
                                                                id="InsuranceNetwork"
                                                                placeholder=""
                                                                invalid={!!errors.InsuranceNetwork}
                                                                maxLength={20}

                                                            />
                                                        )}
                                                    // rules={{ required: "Provider NPI ID is required" }}
                                                    />
                                                    {errors.ProviderNPI && (
                                                        <FormFeedback>
                                                            {errors.ProviderNPI.message}
                                                        </FormFeedback>
                                                    )}
                                                </Col>
                                                )}
                                                {!isDefaultFieldHidden('FeeSchedule') && (
                                                <Col xl={3}>
                                                    <Label className="form-label" htmlFor="FeeSchedule">
                                                        Fee Schedule
                                                    </Label>
                                                    <Controller
                                                        control={control}
                                                        name="FeeSchedule"
                                                        render={({ field }) => (
                                                            <Select<FeeScheduleOption>
                                                                {...field}
                                                                value={FeeScheduleOption.find(option => option.value === field.value) || null}
                                                                options={FeeScheduleOption}
                                                                getOptionLabel={(option) => option.label}
                                                                getOptionValue={(option) => option.value}
                                                                className={classnames("form-control", {
                                                                    "is-invalid": !!errors.FeeSchedule,
                                                                })}
                                                                classNamePrefix="select"
                                                                onChange={(selectedOption: SingleValue<FeeScheduleOption>) => field.onChange(selectedOption?.value)}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                )}
                                            </Row>
                                        </Row>


                                        {/* end of default-fields wrapper */}
                                        </>

                                        <Col md={5} xs={12} >
                                            <div className="d-flex" style={{ marginTop: "5%" }}  >
                                                <Label
                                                    className="form-label me-2"
                                                    for="Screenshot_status"
                                                    style={{ fontSize: "120%", color: "#000" }}
                                                >
                                                    Screenshot Status:
                                                </Label>
                                                <div className="form-check form-check-inline" style={{ display: "flex", alignItems: "center", justifyContent: "center", }}>
                                                    <input
                                                        type="radio"
                                                        name="Screenshot_status"
                                                        id="Screenshot_statusYes"
                                                        value="Yes"
                                                        onChange={handleScreenshot_statusChange}
                                                        className="radio-button"
                                                        style={{ width: "15px", height: "15px", margin: "0 0" }}
                                                    />
                                                    &nbsp;&nbsp;
                                                    <div>
                                                        <Label className="form-check-label" htmlFor="Screenshot_statusYes" style={{ color: "#28a745", marginBottom: "0px" }}>
                                                            YES
                                                        </Label>
                                                    </div>
                                                </div>
                                                <div className="form-check form-check-inline" style={{ display: "flex", alignItems: "center", justifyContent: "center", }}>
                                                    <input
                                                        type="radio"
                                                        name="Screenshot_status"
                                                        id="Screenshot_statusNo"
                                                        value="No"
                                                        onChange={handleScreenshot_statusChange}
                                                        style={{ width: "15px", height: "15px", margin: "0 0" }}
                                                        defaultChecked
                                                        className="radio-button" // Apply class for custom styling
                                                    />
                                                    &nbsp;&nbsp;
                                                    <div>
                                                        <Label className="form-check-label" htmlFor="Screenshot_statusNo" style={{ color: "#dc3545", marginBottom: "0px" }}>
                                                            NO
                                                        </Label>
                                                    </div>


                                                </div>
                                            </div>
                                        </Col>

                                        <Row style={{ marginTop: "3%" }}>
                                            <Col xl={2}>
                                                <Button
                                                    type="submit"
                                                    color="success"
                                                    className="btn-icon d-flex align-items-center justify-content-center"
                                                >
                                                    Save Patient
                                                </Button>
                                            </Col>
                                            <Col xl={2}>
                                                <Button
                                                    className="btn-icon d-flex align-items-center justify-content-center"
                                                    color="danger"
                                                    onClick={() => {
                                                        setSelectedClient("");
                                                        setSelectedClientId("");
                                                        reset();
                                                    }}
                                                >
                                                    Clear
                                                </Button>
                                            </Col>
                                        </Row>

                                    </Row>
                                </CardBody>
                            </div>
                        </>
                    </form>
                    <CardBody>
                        <div className="table-responsive">
                            <div className="dataTables_wrapper">
                                <DataTable
                                    persistTableHead
                                    subHeader={true}
                                    responsive={true}
                                    onSort={handleSort}
                                    data={dataToRender()}
                                    columns={visibleColumns}
                                    paginationServer
                                    customStyles={customStyles}
                                    paginationPerPage={rowsPerPage}
                                    paginationDefaultPage={currentPage}
                                    selectableRows
                                    selectableRowsHighlight
                                    selectableRowsComponent={BootstrapCheckbox as unknown as React.ReactNode}
                                    clearSelectedRows={loading}
                                    onSelectedRowsChange={handleChangecheck}
                                    paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                                    noDataComponent={<CustomLoadingComponent />}
                                    paginationComponent={CustomPagination}
                                    subHeaderComponent={
                                        <CustomHeader
                                            value={value}
                                            rowsPerPage={rowsPerPage}
                                            handleFilter={handleFilter}
                                            handlePerPage={handlePerPage}
                                            columnOptions={columnOptions}
                                            savedColumns={savedColumns}
                                            onColumnsChange={saveColumns}
                                        />
                                    }
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ paddingLeft: '2%' }}>
                                        {store.total && (
                                            <span><strong>{`${startIndex} to ${endIndex} of ${store.total} entries`}</strong></span>
                                        )}
                                    </div>
                                    <div>
                                        <CustomPagination />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </CardBody>
            </Card>
            {loading && (
                <div >
                    <div className="loading-overlay">
                        <div id="page">
                            <div id="container">
                                <div id="ring"></div>
                                <div id="ring"></div>
                                <div id="ring"></div>
                                <div id="ring"></div>
                                <div id="h3" style={{ color: "black" }}>Loading...</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div>
                {eventId1 > 0 && menuActions.includes('delete') && (
                    <div className="customizer-links open">
                        <Nav className=" flex-column nav-pills">
                            <a className="nav-link" onClick={() => DeleteRow(eventId)}>
                                <span>Delete</span>
                                <Trash2 className="trash-icon" size={20} />
                            </a>
                        </Nav>
                    </div>
                )}
                {/* {eventId1 > 0 && (
                    <div className="customizer1-links open">
                        <Nav className="flex-column nav-pills">
                            <a className="nav-link" onClick={() => Exportrow(eventId)}>
                                <span>Export</span>
                                <Download className="download-icon" size={20} />
                            </a>
                        </Nav>
                    </div>
                )} */}
            </div>
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size="xl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    {viewingImage ? (
                        // Full-screen image view
                        <>
                            <img
                                src={`data:image/jpeg;base64,${patientData.image_base64}`}
                                alt="Full Screen View"
                                style={{
                                    width: "100%",
                                    height: "90vh",
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                }}
                            />
                            <button
                                onClick={() => setViewingImage(false)}
                                style={{
                                    position: "absolute",
                                    top: "10px",
                                    right: "10px",
                                    background: "red",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                }}
                            >
                                ✕ Close
                            </button>
                        </>
                    ) : loading ? (
                        <p style={{ color: "black" }}>Loading data...</p>
                    ) : patientData ? (
                        // Main error details view
                        <div className="email-wrapper">
                            <p>{patientData.Payer_name}</p>
                            <h3>Error: {patientData.error_message}</h3>

                            <p>
                                Location:{" "}
                                <a
                                    href={patientData.error_page_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-block",
                                        wordBreak: "break-all", // <== this is the key part
                                        textDecoration: "underline",
                                        color: "#007bff",
                                    }}
                                >
                                    {patientData.error_page_url}
                                </a>
                            </p>


                            {patientData.image_base64 ? (
                                <div style={{ marginTop: "15px" }}>
                                    <img
                                        src={`data:image/jpeg;base64,${patientData.image_base64}`}
                                        alt="Error Screenshot"
                                        style={{
                                            width: "100%",
                                            maxWidth: "500px",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => {
                                            const imageUrl = `data:image/jpeg;base64,${patientData.image_base64}`;
                                            const newWindow = window.open();
                                            if (newWindow) {
                                                newWindow.document.write(
                                                    `<img src="${imageUrl}" style="width:100%;height:auto;object-fit:contain;" />`
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <p>No image available</p>
                            )}
                        </div>
                    ) : (
                        <p>Please select a ticket to view details.</p>
                    )}
                </ModalBody>
            </Modal>

            {/* # United Healthcare OTP */}
            <Modal isOpen={isUnitedHealthcareOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a United Healthcare OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={unitedHealthcareOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isUnitedHealthcareVerifying && value.length <= 6) {
                                setUnitedHealthcareOtp(value);
                                if (unitedHealthcareOtpError) {
                                    setUnitedHealthcareOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isUnitedHealthcareVerifying}
                    />
                    {unitedHealthcareOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {unitedHealthcareOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitUnitedHealthcareOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={unitedHealthcareOtp.length !== 6 || isUnitedHealthcareVerifying}
                    >
                        {isUnitedHealthcareVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Kansas OTP */}
            <Modal isOpen={isKansasOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: 'center', padding: '20px' }}>
                    <h4>Enter a Kansas OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={kansasOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!iskansasOtpVerifying && value.length <= 6) {
                                setKansasOtp(value);
                                if (kansasOtpError) {
                                    setkansasOtpError(''); // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={iskansasOtpVerifying}
                    />
                    {kansasOtpError && (
                        <div style={{ color: 'red', marginTop: '5px', fontSize: '13px' }}>
                            {kansasOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitkansasOtp}
                        style={{ marginTop: '10px', cursor: 'pointer' }}
                        disabled={kansasOtp.length !== 6 || iskansasOtpVerifying}
                    >
                        {iskansasOtpVerifying ? 'Verifying OTP...' : 'Submit'}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Virginia OTP */}
            <Modal isOpen={isVirginiaOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Virginia OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={virginiaOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isVirginiaVerifying && value.length <= 6) {
                                setVirginiaOtp(value);
                                if (virginiaOtpError) {
                                    setVirginiaOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isVirginiaVerifying}
                    />
                    {virginiaOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {virginiaOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitVirginiaOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={virginiaOtp.length !== 6 || isVirginiaVerifying}
                    >
                        {isVirginiaVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Iowa OTP */}
            <Modal isOpen={isIowaOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Iowa OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={iowaOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isIowaOtpVerifying && value.length <= 6) {
                                setIowaOtp(value);
                                if (IowaOtpError) {
                                    setIowaOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isIowaOtpVerifying}
                    />
                    {IowaOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {IowaOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitIowaOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={iowaOtp.length !== 6 || isIowaOtpVerifying}
                    >
                        {isIowaOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Northeast OTP */}
            <Modal isOpen={isNortheastOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Northeast Delta Dental OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={northeastOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isNortheastOtpVerifying && value.length <= 6) {
                                setNortheastOtp(value);
                                if (NortheastOtpError) {
                                    setNortheastOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isNortheastOtpVerifying}
                    />
                    {NortheastOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {NortheastOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitNortheastOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={northeastOtp.length !== 6 || isNortheastOtpVerifying}
                    >
                        {isNortheastOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Georgia OTP */}
            <Modal isOpen={isGeorgiaOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Georgia OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={georgiaOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isgeorgiaVerifying && value.length <= 6) {
                                setGeorgiaOtp(value);
                                if (georgiaOtpError) {
                                    setgeorgiaOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isgeorgiaVerifying}
                    />
                    {georgiaOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {georgiaOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitGeorgiaOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={georgiaOtp.length !== 6 || isgeorgiaVerifying}
                    >
                        {isgeorgiaVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Guardian OTP */}
            <Modal isOpen={isGuardianOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Guardian Life Insurance Co OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={guardianOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isguardianVerifying && value.length <= 6) {
                                setGuardianOtp(value);
                                if (guardianOtpError) {
                                    setguardianOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isguardianVerifying}
                    />
                    {guardianOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {guardianOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitGuardianOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={guardianOtp.length !== 6 || isguardianVerifying}
                    >
                        {isguardianVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Arkansas OTP */}
            <Modal isOpen={isArkansasOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Arkansas OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={arkansasOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isArkansasOtpVerifying && value.length <= 6) {
                                setArkansasOtp(value);
                                if (ArkansasOtpError) {
                                    setArkansasOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isArkansasOtpVerifying}
                    />
                    {ArkansasOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {ArkansasOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitArkansasOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={arkansasOtp.length !== 6 || isArkansasOtpVerifying}
                    >
                        {isArkansasOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Colorado OTP */}
            <Modal isOpen={isColoradoOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Colorado OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={coloradoOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isColoradoOtpVerifying && value.length <= 6) {
                                setColoradoOtp(value);
                                if (ColoradoOtpError) {
                                    setColoradoOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isColoradoOtpVerifying}
                    />
                    {ColoradoOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {ColoradoOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitColoradoOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={coloradoOtp.length !== 6 || isColoradoOtpVerifying}
                    >
                        {isColoradoOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Individual Plan OTP */}
            <Modal isOpen={isIndividual_PlanOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Individual Plan OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={individual_PlanOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Allow only numbers and max 6 digits
                            if (value.length <= 6) {
                                setIndividual_PlanOtp(value);
                                if (Individual_PlanOtpError) {
                                    setIndividual_PlanOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isIndividual_PlanOtpVerifying}
                    />
                    {Individual_PlanOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {Individual_PlanOtpError}
                        </div>
                    )}
                    <Button color="primary" onClick={handleSubmitIndividual_PlanOtp} style={{ marginTop: "10px", cursor: "pointer" }} disabled={individual_PlanOtp.length !== 6 || isIndividual_PlanOtpVerifying}>
                        {isIndividual_PlanOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # New Jersey OTP */}
            <Modal isOpen={isNew_JerseyOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of New Jersey OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={new_JerseyOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Allow only numbers and max 6 digits
                            if (value.length <= 6) {
                                setNew_JerseyOtp(value);
                                if (New_JerseyOtpError) {
                                    setNew_JerseyOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isNew_JerseyOtpVerifying}
                    />
                    {New_JerseyOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {New_JerseyOtpError}
                        </div>
                    )}
                    <Button color="primary" onClick={handleSubmitNew_JerseyOtp} style={{ marginTop: "10px", cursor: "pointer" }} disabled={new_JerseyOtp.length !== 6 || isNew_JerseyOtpVerifying}>
                        {isNew_JerseyOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # New York OTP */}
            <Modal isOpen={isNew_YorkOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of New York OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={new_YorkOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Allow only numbers and max 6 digits
                            if (value.length <= 6) {
                                setNew_YorkOtp(value);
                                if (New_YorkOtpError) {
                                    setNew_YorkOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isNew_YorkOtpVerifying}
                    />
                    {New_YorkOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {New_YorkOtpError}
                        </div>
                    )}
                    <Button color="primary" onClick={handleSubmitNew_YorkOtp} style={{ marginTop: "10px", cursor: "pointer" }} disabled={new_YorkOtp.length !== 6 || isNew_YorkOtpVerifying}>
                        {isNew_YorkOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Massachusetts OTP */}
            <Modal isOpen={isMassachusettsOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Massachusetts OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={massachusettsOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Allow only numbers and max 6 digits
                            if (value.length <= 6) {
                                setMassachusettsOtp(value);
                                if (MassachusettsOtpError) {
                                    setMassachusettsOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isMassachusettsOtpVerifying}
                    />
                    {MassachusettsOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {MassachusettsOtpError}
                        </div>
                    )}
                    <Button color="primary" onClick={handleSubmitMassachusettsOtp} style={{ marginTop: "10px", cursor: "pointer" }} disabled={massachusettsOtp.length !== 6 || isMassachusettsOtpVerifying}>
                        {isMassachusettsOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Washington OTP */}
            <Modal isOpen={isWashingtonOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Washington OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={washingtonOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isWashingtonOtpVerifying && value.length <= 6) {
                                setWashingtonOtp(value);
                                if (WashingtonOtpError) {
                                    setWashingtonOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isWashingtonOtpVerifying}
                    />
                    {WashingtonOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {WashingtonOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitWashingtonOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={washingtonOtp.length !== 6 || isWashingtonOtpVerifying}
                    >
                        {isWashingtonOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Wisconsin OTP */}
            <Modal isOpen={isWisconsinOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Wisconsin OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={wisconsinOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isWisconsinOtpVerifying && value.length <= 6) {
                                setWisconsinOtp(value);
                                if (WisconsinOtpError) {
                                    setWisconsinOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isWisconsinOtpVerifying}
                    />
                    {WisconsinOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {WisconsinOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitWisconsinOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={wisconsinOtp.length !== 6 || isWisconsinOtpVerifying}
                    >
                        {isWisconsinOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Illinois OTP */}
            <Modal isOpen={isIllinoisOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Illinois OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={illinoisOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isIllinoisOtpVerifying && value.length <= 6) {
                                setIllinoisOtp(value);
                                if (IllinoisOtpError) {
                                    setIllinoisOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isIllinoisOtpVerifying}
                    />
                    {IllinoisOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {IllinoisOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitIllinoisOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={illinoisOtp.length !== 6 || isIllinoisOtpVerifying}
                    >
                        {isIllinoisOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Rhode Island OTP */}
            <Modal isOpen={isRhodeIslandOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Rhode Island OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={rhodeIslandOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isRhodeIslandOtpVerifying && value.length <= 6) {
                                setRhodeIslandOtp(value);
                                if (RhodeIslandOtpError) {
                                    setRhodeIslandOtpError("");
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isRhodeIslandOtpVerifying}
                    />
                    {RhodeIslandOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {RhodeIslandOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitRhodeIslandOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={rhodeIslandOtp.length !== 6 || isRhodeIslandOtpVerifying}
                    >
                        {isRhodeIslandOtpVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Oklahoma OTP */}
            <Modal isOpen={isOklahomaOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Delta Dental of Oklahoma OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={oklahomaOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isOklahomaVerifying && value.length <= 6) {
                                setOklahomaOtp(value);
                                if (oklahomaOtpError) {
                                    setOklahomaOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isOklahomaVerifying}
                    />
                    {oklahomaOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {oklahomaOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitOklahomaOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={oklahomaOtp.length !== 6 || isOklahomaVerifying}
                    >
                        {isOklahomaVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Amertias OTP */}
            <Modal isOpen={isAmertiasOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Amertias OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={amertiasOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isAmertiasVerifying && value.length <= 6) {
                                setAmertiasOtp(value);
                                if (AmertiasOtpError) {
                                    setAmertiasOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isAmertiasVerifying}
                    />
                    {AmertiasOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {AmertiasOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitAmertiasOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={amertiasOtp.length !== 6 || isAmertiasVerifying}
                    >
                        {isAmertiasVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Missouri OTP */}
            <Modal isOpen={isMissouriOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Missouri OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={missouriOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isMissouriVerifying && value.length <= 6) {
                                setMissouriOtp(value);
                                if (missouriOtpError) {
                                    setMissouriOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isMissouriVerifying}
                    />
                    {missouriOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {missouriOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitMissouriOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={missouriOtp.length !== 6 || isMissouriVerifying}
                    >
                        {isMissouriVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Toolkit OTP */}
            <Modal isOpen={isToolkitOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Toolkit OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={toolkitOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isToolkitVerifying && value.length <= 6) {
                                setToolkitOtp(value);
                                if (toolkitOtpError) {
                                    setToolkitOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isToolkitVerifying}
                    />
                    {toolkitOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {toolkitOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitToolkitOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={toolkitOtp.length !== 6 || isToolkitVerifying}
                    >
                        {isToolkitVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            {/* # Cigna OTP */}
            <Modal isOpen={isCignaOTPModalOpen} backdrop="static" keyboard={false} size="sl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <h4>Enter a Cigna OTP</h4>
                    <br />
                    <Input
                        type="text"
                        value={cignaOtp}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!isCignaVerifying && value.length <= 6) {
                                setCignaOtp(value);
                                if (cignaOtpError) {
                                    setCignaOtpError("");   // clear error while typing
                                }
                            }
                        }}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isCignaVerifying}
                    />
                    {cignaOtpError && (
                        <div style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                            {cignaOtpError}
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmitCignaOtp}
                        style={{ marginTop: "10px", cursor: "pointer" }}
                        disabled={cignaOtp.length !== 6 || isCignaVerifying}
                    >
                        {isCignaVerifying ? "Verifying OTP..." : "Submit"}
                    </Button>
                </ModalBody>
            </Modal>

            <Modal isOpen={editOpen} toggle={() => setIsEditOpen(false)} size="xl" centered>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                    <Row style={{ marginTop: "2%" }}>
                        <Col xl={12}>
                            <h4><b>Edit Patient Details:</b></h4>
                        </Col>
                    </Row>
                    <br />
                    <Row>
                        <Col xl={4}>
                            <Label className="form-label" htmlFor="firstName">First Name</Label>
                            <Input
                                type="text"
                                name="firstName"
                                value={formValues.firstName}
                                onChange={handleInputChange}
                            />
                        </Col>
                        <Col xl={4}>
                            <Label className="form-label" htmlFor="middleName">Middle Name</Label>
                            <Input
                                type="text"
                                name="middleName"
                                value={formValues.middleName}
                                onChange={handleInputChange}
                            />
                        </Col>
                        <Col xl={4}>
                            <Label className="form-label" htmlFor="lastName">Last Name</Label>
                            <Input
                                type="text"
                                name="lastName"
                                value={formValues.lastName}
                                onChange={handleInputChange}
                            />
                        </Col>
                    </Row>
                    <Row style={{ marginTop: "2%" }}>
                        <Col xl={4}>
                            <Label className="form-label" htmlFor="memberId">Member ID</Label>
                            <Input
                                type="text"
                                name="memberId"
                                value={formValues.memberId}
                                onChange={handleInputChange}
                            />
                        </Col>
                        <Col xl={4}>
                            <Label className="form-label" htmlFor="dob">Date of Birth</Label>
                            <Input
                                type="text"
                                name="dob"
                                value={formValues.dob}
                                onChange={handleInputChange}
                            />
                        </Col>
                        <Col xl={4}>
                            <Label className="form-label" htmlFor="providerNpi">Provider / Group NPI</Label>
                            <Input
                                type="text"
                                name="providerNpi"
                                value={formValues.providerNpi}
                                onChange={handleInputChange}
                            />
                        </Col>
                    </Row>
                    <br />
                    <Button color="primary" onClick={handleSubmitEditdata} style={{ marginTop: "10px" }}>
                        Update
                    </Button>
                </ModalBody>
            </Modal>

            <div>
                <Modal
                    style={{ maxWidth: "1700px", maxHeight: "1000px" }}
                    isOpen={scrollInnerModal}
                    toggle={() => setScrollInnerModal(!scrollInnerModal)}
                    className="modal-dialog-centered modal-lg"
                >
                    <ModalHeader toggle={() => setScrollInnerModal(!scrollInnerModal)}>
                        Manual Entries Audit Log
                    </ModalHeader>
                    <ModalBody>
                        <div style={{ maxHeight: "600px", overflow: "auto" }}>
                            {filteredAuditData.length > 0 ? (
                                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#f2f2f2" }}>
                                            <th className="audit-tableHeaderStyle">Date & Time</th>
                                            <th className="audit-tableHeaderStyle">Action</th>
                                            <th className="audit-tableHeaderStyle">Data Set</th>
                                            <th className="audit-tableHeaderStyle">Changed By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAuditData
                                            .sort(
                                                (a: AuditData, b: AuditData) =>
                                                    new Date(b.Modified).getTime() - new Date(a.Modified).getTime()
                                            )
                                            .map((item: AuditData, index: number) => {
                                                // Get all field names for header
                                                const fieldNames = Array.isArray(item.JsonData)
                                                    ? item.JsonData.map((d) => d.field)
                                                    : [];

                                                // Map field values for the row
                                                const fieldValues = Array.isArray(item.JsonData)
                                                    ? item.JsonData.map((d) => {
                                                        if (item.Action === "EDIT") {
                                                            return Array.isArray(d.after)
                                                                ? d.after.join(", ")
                                                                : d.after && d.after.value
                                                                    ? d.after.value
                                                                    : d.after;
                                                        } else if (item.Action === "ADD") {
                                                            return Array.isArray(d.value)
                                                                ? d.value.join(", ")
                                                                : d.value && d.value.value
                                                                    ? d.value.value
                                                                    : d.value;
                                                        } else if (item.Action === "VIEW") {
                                                            return d.value || JSON.stringify(d, null, 2);
                                                        } else {
                                                            return "";
                                                        }
                                                    })
                                                    : [];

                                                return (
                                                    <tr key={index}>
                                                        {/* Date & Time */}
                                                        <td className="audit-tableDataStyle">
                                                            {moment
                                                                .utc(item.Modified)
                                                                .tz(moment.tz.guess())
                                                                .format("MM/DD/YYYY hh:mm:ss A")}
                                                        </td>

                                                        {/* Action */}
                                                        <td className="audit-tableDataStyle">{item.Action}</td>

                                                        {/* Data Set (Nested Table with field as header and values as row) */}
                                                        <td className="audit-tableDataStyle">
                                                            {Array.isArray(item.JsonData) ? (
                                                                item.Action === "EDIT" ? (
                                                                    // EDIT: Old style table with Before/After
                                                                    <table style={{ borderCollapse: "collapse", width: "100%" }}>
                                                                        <thead>
                                                                            <tr style={{ backgroundColor: "#f2f2f2" }}>
                                                                                <th className="audit-tableHeaderStyle">Field</th>
                                                                                <th className="audit-tableHeaderStyle">Before</th>
                                                                                <th className="audit-tableHeaderStyle">After</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {item.JsonData
                                                                                .sort((a: JsonData, b: JsonData) => a.field.localeCompare(b.field))
                                                                                .map((dataItem: JsonData, dataIndex: number) => (
                                                                                    <tr key={dataIndex}>
                                                                                        <td className="audit-tableDataStyle">{dataItem.field}</td>
                                                                                        <td className="audit-tableDataStyle">
                                                                                            {Array.isArray(dataItem.before)
                                                                                                ? dataItem.before.join(", ")
                                                                                                : dataItem.before && dataItem.before.value
                                                                                                    ? dataItem.before.value
                                                                                                    : dataItem.before}
                                                                                        </td>
                                                                                        <td className="audit-tableDataStyle">
                                                                                            {Array.isArray(dataItem.after)
                                                                                                ? dataItem.after.join(", ")
                                                                                                : dataItem.after && dataItem.after.value
                                                                                                    ? dataItem.after.value
                                                                                                    : dataItem.after}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    // ADD / VIEW: Field as header, value as row
                                                                    (() => {
                                                                        const fieldNames = item.JsonData.map((d) => d.field);
                                                                        const fieldValues = item.JsonData.map((d) => {
                                                                            if (item.Action === "ADD") {
                                                                                return Array.isArray(d.value)
                                                                                    ? d.value.join(", ")
                                                                                    : d.value && d.value.value
                                                                                        ? d.value.value
                                                                                        : d.value;
                                                                            } else if (item.Action === "VIEW") {
                                                                                return JSON.stringify(d.value) || JSON.stringify(d, null, 2);
                                                                            } else {
                                                                                return "";
                                                                            }
                                                                        });
                                                                        return (
                                                                            <table style={{ borderCollapse: "collapse", width: "100%" }}>
                                                                                <thead>
                                                                                    <tr style={{ backgroundColor: "#f2f2f2" }}>
                                                                                        {fieldNames.map((field, idx) => (
                                                                                            <th key={idx} className="audit-tableHeaderStyle">{field}</th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <tr>
                                                                                        {fieldValues.map((val, idx) => (
                                                                                            <td key={idx} className="audit-tableDataStyle">{val}</td>
                                                                                        ))}
                                                                                    </tr>
                                                                                </tbody>
                                                                            </table>
                                                                        );
                                                                    })()
                                                                )
                                                            ) : (
                                                                <pre>{JSON.stringify(item.JsonData, null, 2)}</pre>
                                                            )}
                                                        </td>


                                                        {/* Changed By */}
                                                        <td className="audit-tableDataStyle">{item.UserName}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            ) : (
                                <div>
                                    <center>No data available</center>
                                </div>
                            )}
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        </Fragment>
    );
}

export default Bulkupload;
